/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */


/* ------------------------------------------------------------------
   Linked variants: instant product swap.
   Tag-driven sibling-product buttons (snippets/linked-variants.liquid)
   now swap in place via Shopify's Section Rendering API — only the
   product sections re-render, prefetched on hover, animated with the
   View Transitions API. Feels like a native variant change; falls back
   to plain navigation on any miss (different template, fetch error).
   ------------------------------------------------------------------ */
(() => {
  const CACHE_TTL = 600000; /* 10 min — prefetched siblings must outlive spec-reading (I.1.1, 8/23) */
  const cache = new Map();

  const swappableWrappers = () => {
    const wrappers = [];
    const seen = new Set();
    const add = (el) => {
      const w = el && el.closest('[id^="shopify-section-"]');
      if (w && !seen.has(w.id)) { seen.add(w.id); wrappers.push(w); }
    };
    add(document.querySelector('section[data-section-type="product"]'));
    add(document.querySelector('.jdgm-rev-widg'));
    add(document.querySelector('.brand-all'));
    /* recommendations stay: sibling variants share the same complements
       and some return an empty list, which blanked the row on swap */
    return wrappers;
  };

  const fetchSections = (handle) => {
    const hit = cache.get(handle);
    if (hit && Date.now() - hit.t < CACHE_TTL) return hit.promise;
    const wrappers = swappableWrappers();
    if (!wrappers.length) return null;
    const ids = wrappers.map((w) => w.id.replace('shopify-section-', ''));
    const promise = fetch(`/products/${handle}?sections=${ids.join(',')}`, { credentials: 'same-origin' })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); });
    cache.set(handle, { t: Date.now(), promise });
    promise.catch(() => cache.delete(handle));
    return promise;
  };

  const currentSuffixClass = () =>
    [...document.body.classList].find((c) => c.indexOf('product-') === 0) || 'product-';

  /* prefetch on hover/touch */
  document.addEventListener('pointerover', (event) => {
    const item = event.target.closest('.otherColor');
    if (!item) return;
    const handle = item.getAttribute('data-handle');
    if (!handle || item.querySelector('a.active')) return;
    if (('product-' + (item.getAttribute('data-suffix') || '')) !== currentSuffixClass()) return;
    try { fetchSections(handle); } catch (e) { /* prefetch is best-effort */ }
  }, { passive: true });

  /* I.1.1 (8/23): swaps are server-render-bound (~500-750ms Liquid per
     sibling, cf-cache DYNAMIC on live AND draft) and hover gives ~0ms head
     start on touch. Prefetch every same-template sibling while the shopper
     reads the page, staggered to stay polite; clicks then hit the warm
     promise cache (~150-300ms felt). */
  const prefetchAllSiblings = () => {
    if (navigator.connection && navigator.connection.saveData) return;
    const cur = currentSuffixClass();
    const seen = new Set();
    const handles = [];
    document.querySelectorAll('.otherColor[data-handle]').forEach((item) => {
      const handle = item.getAttribute('data-handle');
      if (!handle || seen.has(handle) || item.querySelector('a.active')) return;
      if (('product-' + (item.getAttribute('data-suffix') || '')) !== cur) return;
      seen.add(handle);
      handles.push(handle);
    });
    handles.slice(0, 12).forEach((h, i) => {
      setTimeout(() => { try { fetchSections(h); } catch (e) { /* best-effort */ } }, 600 * i);
    });
  };
  if (document.querySelector('.otherColor[data-handle]')) {
    if ('requestIdleCallback' in window) requestIdleCallback(() => setTimeout(prefetchAllSiblings, 800));
    else setTimeout(prefetchAllSiblings, 2500);
  }

  document.addEventListener('click', (event) => {
    const item = event.target.closest('.otherColor');
    if (!item) return;
    event.preventDefault();
    if (item.querySelector('a.active')) return;
    const handle = item.getAttribute('data-handle');
    if (!handle) return;
    const url = '/products/' + handle;

    /* inside a quick-view modal: swap the modal's product, not the page */
    const qvModal = item.closest('[id^="modal-quick-view"]');
    if (qvModal && typeof window.hodLoadQuickView === 'function') {
      window.hodLoadQuickView(qvModal, url);
      return;
    }

    /* different template suffix renders different sections — navigate */
    if (('product-' + (item.getAttribute('data-suffix') || '')) !== currentSuffixClass()) {
      window.location.href = url;
      return;
    }

    const request = fetchSections(handle);
    if (!request) { window.location.href = url; return; }

    request.then((sections) => {
      const apply = () => {
        /* reviews are vendor-aggregated: preserve the fully-initialized
           widget across same-vendor swaps (jdgm's async rebuild of the
           swapped-in copy comes back with missing pieces) */
        const keepReviews = document.getElementById('judgeme_product_reviews');
        const keepWrap = document.querySelector('.product-block-list__item--reviews[data-vendor]');
        const keepVendor = keepWrap ? keepWrap.getAttribute('data-vendor') : null;
        const swappedIds = [];
        Object.keys(sections).forEach((id) => {
          const target = document.getElementById('shopify-section-' + id);
          if (target && sections[id]) {
            /* tell the theme's SectionContainer to tear down this section's JS
               (ProductGallery/Flickity etc.) before its DOM disappears —
               otherwise the swapped-in gallery has no instance and the
               arrows/thumbnails are dead until a full reload */
            try { document.dispatchEvent(new CustomEvent('shopify:section:unload', { detail: { sectionId: id } })); } catch (e) { /* non-critical */ }
            target.outerHTML = sections[id];
            swappedIds.push(id);
          }
        });
        const freshWrap = document.querySelector('.product-block-list__item--reviews[data-vendor]');
        const freshReviews = document.getElementById('judgeme_product_reviews');
        if (keepReviews && freshReviews && freshWrap && keepVendor && freshWrap.getAttribute('data-vendor') === keepVendor) {
          freshReviews.replaceWith(keepReviews);
        }
        /* re-instantiate the theme's section JS on the fresh markup (the same
           events the theme editor fires; SectionContainer listens on the
           storefront too). Non-section wrappers (jdgm, brand-all) no-op. */
        swappedIds.forEach((id) => {
          const fresh = document.getElementById('shopify-section-' + id);
          if (fresh) {
            try { fresh.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId: id } })); } catch (e) { /* non-critical */ }
          }
        });
        const heroImg = document.querySelector('section[data-section-type="product"] img');
        if (heroImg) {
          heroImg.loading = 'eager';
          heroImg.decoding = 'async';
          heroImg.setAttribute('fetchpriority', 'high');
        }
        const title = item.getAttribute('data-title');
        if (title) document.title = title;
        history.pushState({ hodLinkedVariant: true }, '', url);
        /* Third-party scripts only scan the DOM once at page load, so the
           swapped markup needs explicit re-initialization. */
        const reinitThirdParty = () => {
          /* Judge.me markup ships display:none "temp hiding" styles that its
             loader removes once at page load; swapped-in markup re-adds them
             and nothing removes them again, leaving the widget invisible. */
          document.querySelectorAll('style.jdgm-temp-hiding-style').forEach((s) => s.remove());
          /* review widget body ships display:none inline; jdgm's loader sets
             it to block once at page load — mirror that for swapped markup */
          document.querySelectorAll('.jdgm-legacy-widget-content').forEach((el) => { el.style.display = 'block'; });
          if (window.jdgm) {
            try {
              if (typeof window.jdgm.init === 'function') window.jdgm.init();
              if (typeof window.jdgm.parseScrollDisabledWidgets === 'function') window.jdgm.parseScrollDisabledWidgets();
              if (typeof window.jdgm.customizeBadges === 'function') window.jdgm.customizeBadges();
            } catch (e) { /* non-critical */ }
          }
          if (window.jdgmCacheServer && typeof window.jdgmCacheServer.reloadAll === 'function') {
            try { window.jdgmCacheServer.reloadAll(); } catch (e) { /* non-critical */ }
          }
          if (window.affirm && window.affirm.ui && typeof window.affirm.ui.refresh === 'function') {
            try { window.affirm.ui.refresh(); } catch (e) { /* non-critical */ }
          }
          if (typeof window.hodChipbarInit === 'function') {
            try { window.hodChipbarInit(); } catch (e) { /* non-critical */ }
          }
          if (typeof window.hodReviewsTitle === 'function') {
            try { window.hodReviewsTitle(); } catch (e) { /* non-critical */ }
          }
          if (typeof window.hodReviewsPolish === 'function') {
            try { window.hodReviewsPolish(); } catch (e) { /* non-critical */ }
          }
          if (typeof window.hodFractionalStars === 'function') {
            try { window.hodFractionalStars(); } catch (e) { /* non-critical */ }
          }
          if (typeof window.hodReviewsObserve === 'function') {
            try { window.hodReviewsObserve(); } catch (e) { /* non-critical */ }
          }
        };
        reinitThirdParty();
        /* second pass: affirm/jdgm may still be booting on slow loads */
        setTimeout(reinitThirdParty, 600);
      };
      /* no View Transition: its full-page cross-fade reads as a brief
         screen dim on mobile (Stas 8.1) — swap instantly instead */
      apply();
    }).catch(() => { window.location.href = url; });
  });

  /* back/forward after in-place swaps: reload for a consistent page */
  window.addEventListener('popstate', () => {
    if (history.state && history.state.hodLinkedVariant) {
      window.location.reload();
    } else if (document.referrer === '' || performance.getEntriesByType('navigation')[0]) {
      window.location.reload();
    }
  });
})();

/* "Show More" expander for long color/option groups */
document.addEventListener('click', function (event) {
  var btn = event.target.closest('.Show_mor_Color');
  if (!btn) return;
  var box = btn.closest('.add_show_box');
  if (box) box.querySelectorAll('.show_handler').forEach(function (el) { el.style.display = 'inline-block'; });
  btn.style.display = 'none';
});

/* Legacy global money formatter — used by the bundle builder JS (50 call sites).
   Old theme.js provided this; Warehouse 7 does not. Uses the shop money format
   exposed on window.theme by layout/theme.liquid. */
window.Shopify = window.Shopify || {};
if (typeof window.Shopify.formatMoney !== 'function') {
  window.Shopify.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || (window.theme && window.theme.moneyFormat) || '${{amount}}';

    function defaultOption(opt, def) { return typeof opt === 'undefined' ? def : opt; }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
    }
    return formatString.replace(placeholderRegex, value);
  };
}

/* ------------------------------------------------------------------
   Range filter (numeric list filters like Cu. Ft. / Depth).
   Sliders + inputs sync a hidden checkbox per list value; checking the
   range's members triggers the theme's normal filter-change reload.
   ------------------------------------------------------------------ */
class RangeFilter extends HTMLElement {
  connectedCallback() {
    this.step = parseFloat(this.getAttribute('data-step') || '0.1');
    this.decimals = this.step >= 1 ? 0 : 1;
    this.checkboxes = Array.from(this.querySelectorAll('.range-filter__checkbox'));

    this.rangeLowerBound = this.querySelector('.range-filter__range-group input:first-child');
    this.rangeHigherBound = this.querySelector('.range-filter__range-group input:last-child');
    this.textInputLowerBound = this.querySelector('.range-filter__input:first-child input');
    this.textInputHigherBound = this.querySelector('.range-filter__input:last-child input');

    this.textInputLowerBound.addEventListener('focus', () => this.textInputLowerBound.select());
    this.textInputHigherBound.addEventListener('focus', () => this.textInputHigherBound.select());

    this.textInputLowerBound.addEventListener('change', (event) => {
      let val = parseFloat(event.target.value);
      val = Math.max(Math.min(val, parseFloat(this.textInputHigherBound.value || event.target.max) - this.step), parseFloat(event.target.min));
      event.target.value = val;
      this.rangeLowerBound.value = val;
      this.rangeLowerBound.parentElement.style.setProperty('--range-min', ((val - parseFloat(this.rangeLowerBound.min)) / (parseFloat(this.rangeLowerBound.max) - parseFloat(this.rangeLowerBound.min))) * 100 + '%');
      this._updateCheckboxes();
    });

    this.textInputHigherBound.addEventListener('change', (event) => {
      let val = parseFloat(event.target.value);
      val = Math.min(Math.max(val, parseFloat(this.textInputLowerBound.value || event.target.min) + this.step), parseFloat(event.target.max));
      event.target.value = val;
      this.rangeHigherBound.value = val;
      this.rangeHigherBound.parentElement.style.setProperty('--range-max', ((val - parseFloat(this.rangeHigherBound.min)) / (parseFloat(this.rangeHigherBound.max) - parseFloat(this.rangeHigherBound.min))) * 100 + '%');
      this._updateCheckboxes();
    });

    this.rangeLowerBound.addEventListener('change', (event) => {
      this.textInputLowerBound.value = parseFloat(event.target.value).toFixed(this.decimals);
      this._updateCheckboxes();
    });

    this.rangeHigherBound.addEventListener('change', (event) => {
      this.textInputHigherBound.value = parseFloat(event.target.value).toFixed(this.decimals);
      this._updateCheckboxes();
    });

    this.rangeLowerBound.addEventListener('input', (event) => {
      this.dispatchEvent(new CustomEvent('collection:abort-loading', { bubbles: true }));
      let val = parseFloat(event.target.value);
      val = Math.min(val, parseFloat(this.textInputHigherBound.value || event.target.max) - this.step);
      event.target.value = val;
      const rangeMin = parseFloat(event.target.min);
      const rangeMax = parseFloat(event.target.max);
      event.target.parentElement.style.setProperty('--range-min', ((val - rangeMin) / (rangeMax - rangeMin)) * 100 + '%');
      this.textInputLowerBound.value = val.toFixed(this.decimals);
    });

    this.rangeHigherBound.addEventListener('input', (event) => {
      this.dispatchEvent(new CustomEvent('collection:abort-loading', { bubbles: true }));
      let val = parseFloat(event.target.value);
      val = Math.max(val, parseFloat(this.textInputLowerBound.value || event.target.min) + this.step);
      event.target.value = val;
      const rangeMin = parseFloat(event.target.min);
      const rangeMax = parseFloat(event.target.max);
      event.target.parentElement.style.setProperty('--range-max', ((val - rangeMin) / (rangeMax - rangeMin)) * 100 + '%');
      this.textInputHigherBound.value = val.toFixed(this.decimals);
    });
  }

  _updateCheckboxes() {
    const low = parseFloat(this.textInputLowerBound.value);
    const high = parseFloat(this.textInputHigherBound.value);
    let firstChanged = null;

    this.checkboxes.forEach((cb) => {
      const val = parseFloat(cb.getAttribute('data-numeric-value'));
      const shouldBeChecked = val >= low && val <= high;
      if (cb.checked !== shouldBeChecked) {
        cb.checked = shouldBeChecked;
        firstChanged = firstChanged || cb;
      }
    });

    if (firstChanged) {
      firstChanged.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}
if (!window.customElements.get('range-filter')) {
  window.customElements.define('range-filter', RangeFilter);
}

/* Merged filter: clear a whole sub-group (e.g. a Depth or Cu. Ft. range) from its active-filter pill */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-action="clear-merged-group"]');
  if (!btn) return;
  const group = btn.getAttribute('data-merged-group');
  const param = btn.getAttribute('data-merged-param');
  const form = document.getElementById('desktop-filters-form');
  if (!form) return;
  const cleared = [];
  form.querySelectorAll('details.hod-spec-group').forEach((groupEl) => {
    const label = groupEl.querySelector('.hod-spec-group__label');
    if (label && label.textContent.trim() === group) {
      groupEl.querySelectorAll('input[type="checkbox"]:checked').forEach((box) => {
        box.checked = false;
        cleared.push(box);
      });
    }
  });
  if (!cleared.length && param) {
    /* Fallback: single-group merged filter renders without a sub-title — clear the whole filter */
    form.querySelectorAll('input[type="checkbox"][name="' + CSS.escape(param) + '"]:checked').forEach((box) => {
      box.checked = false;
      cleared.push(box);
    });
  }
  if (cleared.length) {
    cleared[0].dispatchEvent(new Event('change', { bubbles: true }));
  }
});

/* ------------------------------------------------------------------
   Bundle builder quick view. 7.0.1's theme.js only binds
   open-quick-view inside its own section classes (collection, search,
   recommendations) — the bundle builder isn't one, so its injected
   "View Details" buttons opened an empty modal. This fills it the same
   way theme.js does: fetch the product page, lift the pre-rendered
   quick-view section out of its <template>, drop it in the modal.
   ------------------------------------------------------------------ */
document.addEventListener('click', (event) => {
  const target = event.target.closest('[data-secondary-action="open-quick-view"]');
  if (!target || !target.closest('.bundle-builder--wrapper, .product-block-list__item--complementary')) return;

  const productUrl = target.getAttribute('data-product-url');
  if (!productUrl) return;

  if (window.matchMedia('(max-width: 999px)').matches) {
    window.location.href = productUrl;
    return;
  }

  const modal = document.getElementById(target.getAttribute('aria-controls'));
  if (!modal) return;
  modal.classList.add('is-loading');

  fetch(productUrl, { credentials: 'same-origin' })
    .then((response) => response.text())
    .then((content) => {
      const doc = new DOMParser().parseFromString(content, 'text/html');
      let quickView = doc.querySelector('[data-section-type="product-quick-view"]');
      if (!quickView) {
        for (const tpl of doc.querySelectorAll('template')) {
          quickView = tpl.content.querySelector('[data-section-type="product-quick-view"]');
          if (quickView) break;
        }
      }
      if (!quickView) {
        modal.classList.remove('is-loading');
        window.location.href = productUrl;
        return;
      }
      modal.querySelector('.modal__inner').replaceChildren(document.importNode(quickView, true));
      modal.classList.remove('is-loading');
    })
    .catch(() => {
      modal.classList.remove('is-loading');
    });
});

/* E.1: shared harvest — read the data-stock/data-eta/data-sale attributes
   hod-stock-attrs stamps on JS add buttons, so JSON /cart/add.js calls carry
   the same properties[Stock]/[ETA]/[Sale] the buy forms post. */
window.hodStockProps = (el) => {
  const props = {};
  if (!el) return props;
  const s = el.getAttribute('data-stock');
  const e = el.getAttribute('data-eta');
  const f = el.getAttribute('data-sale');
  if (s) props['Stock'] = s;
  if (e) props['ETA'] = e;
  /* checkout renders "name: value" — sentence as name (see product-buy-buttons) */
  if (f) props['This item is final sale'] = 'non-returnable';
  return props;
};

/* ------------------------------------------------------------------
   Frequently Bought Together (fbt-related.liquid): checkbox totals
   and multi add-to-cart via /cart/add.js. The mini-cart re-renders
   through the theme's cart:refresh event.
   ------------------------------------------------------------------ */
(() => {
  const money = (cents) => (typeof Shopify !== 'undefined' && Shopify.formatMoney)
    ? Shopify.formatMoney(cents)
    : '$' + (cents / 100).toFixed(2);

  const updateTotal = (box) => {
    const checked = [...box.querySelectorAll('.fbt__checkbox:checked')];
    const total = checked.reduce((sum, cb) => sum + parseInt(cb.getAttribute('data-price'), 10), 0);
    const compare = checked.reduce((sum, cb) => sum + (parseInt(cb.getAttribute('data-cprice'), 10) || parseInt(cb.getAttribute('data-price'), 10)), 0);
    const totalEl = box.querySelector('[data-fbt-total]');
    if (totalEl) totalEl.textContent = money(total);
    const compareEl = box.querySelector('[data-fbt-compare]');
    if (compareEl) {
      compareEl.textContent = compare > total ? money(compare) : '';
      compareEl.style.display = compare > total ? '' : 'none';
    }
    const savingsEl = box.querySelector('[data-fbt-savings]');
    if (savingsEl) {
      const pct = compare > total ? Math.round((1 - total / compare) * 100) : 0;
      savingsEl.textContent = 'Save ' + pct + '%';
      savingsEl.hidden = pct <= 0;
    }
    const btn = box.querySelector('[data-fbt-add]');
    if (btn) btn.disabled = checked.length === 0;
  };

  const init = () => document.querySelectorAll('[data-fbt]').forEach(updateTotal);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('change', (event) => {
    if (!event.target.classList.contains('fbt__checkbox')) return;
    updateTotal(event.target.closest('[data-fbt]'));
  });

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-fbt-add]');
    if (!btn) return;
    const box = btn.closest('[data-fbt]');
    const items = [...box.querySelectorAll('.fbt__checkbox:checked')].map((cb) => ({
      id: parseInt(cb.getAttribute('data-variant-id'), 10),
      quantity: 1,
      /* _added_via: staff-visible attribution on the admin order page (hidden from customers) */
      properties: Object.assign(window.hodStockProps(cb), { _added_via: 'Frequently Bought Together' })
    }));
    if (!items.length) return;

    btn.disabled = true;
    const originalLabel = btn.getAttribute('data-label') || btn.textContent;
    try {
      const res = await fetch(window.routes && window.routes.cartUrl ? window.routes.cartUrl + '/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error('add failed');
      document.documentElement.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { quantity: items.length, fbtAdd: true, productAdded: true } })); /* productAdded opens the drawer (theme._onProductAdded gates on it) */
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      btn.textContent = btn.getAttribute('data-label-added') || 'Added ✓';
      setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 2500);
    } catch (e) {
      btn.textContent = 'Error — try again';
      setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 2500);
    }
  });
})();


/* Mirror the mini-cart open state onto <body> so CSS can react without
   depending on :has() (older Safari, and belt-and-braces elsewhere).
   Drawer round 8: while open on phones, pin the drawer's bottom to the
   REAL viewport bottom. The CSS height (100dvh - header var) guesses
   the drawer's absolute top offset and left a dead gap under the
   footer; offsetTop is layout-based, so the open transition's scale
   can't skew the measurement. */
(() => {
  const sizeDrawer = (mc) => {
    if (window.innerWidth > 640 || mc.getAttribute('aria-hidden') !== 'false') {
      mc.style.removeProperty('height');
      mc.style.removeProperty('max-height');
      return;
    }
    const parentTop = mc.offsetParent ? mc.offsetParent.getBoundingClientRect().top : 0;
    const top = parentTop + mc.offsetTop;
    const h = Math.max(300, Math.round(window.innerHeight - top));
    mc.style.setProperty('height', h + 'px', 'important');
    mc.style.setProperty('max-height', h + 'px', 'important');
  };
  const sync = () => {
    const mc = document.getElementById('mini-cart');
    document.body.classList.toggle('cart-drawer-open', !!mc && mc.getAttribute('aria-hidden') === 'false');
    if (mc) sizeDrawer(mc);
  };
  const arm = () => {
    const mc = document.getElementById('mini-cart');
    if (!mc) return;
    new MutationObserver(sync).observe(mc, { attributes: true, attributeFilter: ['aria-hidden'] });
    window.addEventListener('resize', sync);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', sync);
    sync();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }
})();

/* ------------------------------------------------------------------
   PDP gallery: desktop prev/next arrows + expand-to-lightbox
   (restores the 2.3.4-era UX on 7.0.1's gallery).
   - Arrows drive the theme's own thumbnail handler, so carousel and
     thumbnails stay in sync with zero access to the Flickity instance.
   - Expand opens a dependency-free lightbox using the 1600px data-zoom
     URLs the theme already renders on every image.
   - The Drift hover-zoom is disabled in CSS (custom.css).
   ------------------------------------------------------------------ */
(() => {
  const ICONS = {
    prev: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M15 4l-8 8 8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 4l8 8-8 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    expand: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    close: '<svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
  };

  const visibleThumbs = (gallery) =>
    [...gallery.querySelectorAll('.product-gallery__thumbnail:not(.is-filtered)')];

  const step = (gallery, dir) => {
    const thumbs = visibleThumbs(gallery);
    if (thumbs.length < 2) return;
    const current = thumbs.findIndex((t) => t.classList.contains('is-nav-selected'));
    const next = (current + dir + thumbs.length) % thumbs.length;
    thumbs[next].dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
  };

  const galleryImages = (gallery) =>
    [...gallery.querySelectorAll('.product-gallery__carousel-item:not(.is-filtered)[data-media-type="image"] .product-gallery__image')];

  /* ---- lightbox ---- */
  let lb = null;
  const openLightbox = (gallery) => {
    const imgs = galleryImages(gallery);
    if (!imgs.length) return;
    let index = imgs.findIndex((img) => img.closest('.product-gallery__carousel-item').classList.contains('is-selected'));
    if (index < 0) index = 0;

    lb = document.createElement('div');
    lb.className = 'hod-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Image gallery');
    lb.innerHTML = `
      <button type="button" class="hod-lightbox__close" aria-label="Close">${ICONS.close}</button>
      <button type="button" class="hod-lightbox__nav hod-lightbox__nav--prev" aria-label="Previous">${ICONS.prev}</button>
      <img class="hod-lightbox__image" alt="">
      <button type="button" class="hod-lightbox__nav hod-lightbox__nav--next" aria-label="Next">${ICONS.next}</button>
      <div class="hod-lightbox__count"></div>`;
    document.body.appendChild(lb);
    document.documentElement.classList.add('hod-lightbox-open');
    const lbTrigger = document.activeElement;
    setTimeout(() => { const c = lb && lb.querySelector('.hod-lightbox__close'); if (c) c.focus(); }, 0);

    const imgEl = lb.querySelector('.hod-lightbox__image');
    const countEl = lb.querySelector('.hod-lightbox__count');
    const show = (i) => {
      index = (i + imgs.length) % imgs.length;
      const src = imgs[index].getAttribute('data-zoom') || imgs[index].currentSrc;
      imgEl.src = src;
      imgEl.alt = imgs[index].alt || '';
      countEl.textContent = (index + 1) + ' / ' + imgs.length;
      lb.querySelectorAll('.hod-lightbox__nav').forEach((n) => { n.style.display = imgs.length > 1 ? '' : 'none'; });
    };
    show(index);

    const close = () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.classList.remove('hod-lightbox-open');
      lb.remove();
      lb = null;
      if (lbTrigger && lbTrigger.focus) lbTrigger.focus();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') show(index - 1);
      if (e.key === 'ArrowRight') show(index + 1);
    };
    document.addEventListener('keydown', onKey);
    lb.addEventListener('click', (e) => {
      if (e.target.closest('.hod-lightbox__nav--prev')) { show(index - 1); return; }
      if (e.target.closest('.hod-lightbox__nav--next')) { show(index + 1); return; }
      if (e.target.closest('.hod-lightbox__close') || e.target === lb || e.target === imgEl) close();
    });
  };

  /* ---- inject controls into each gallery ---- */
  const enhance = () => {
    document.querySelectorAll('.product-gallery__carousel-wrapper:not([data-hod-gallery])').forEach((wrapper) => {
      wrapper.setAttribute('data-hod-gallery', '');
      const gallery = wrapper.closest('.product-gallery');
      const carousel = wrapper.querySelector('.product-gallery__carousel');
      if (!carousel) return;

      if (galleryImages(gallery).length) {
        const expand = document.createElement('button');
        expand.type = 'button';
        expand.className = 'hod-gallery-expand';
        expand.setAttribute('aria-label', 'View larger image');
        expand.innerHTML = ICONS.expand;
        expand.addEventListener('click', () => openLightbox(gallery));
        wrapper.appendChild(expand);
      }

      if (parseInt(carousel.getAttribute('data-media-count'), 10) > 1) {
        [['prev', -1], ['next', 1]].forEach(([name, dir]) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'hod-gallery-arrow hod-gallery-arrow--' + name;
          btn.setAttribute('aria-label', name === 'prev' ? 'Previous image' : 'Next image');
          btn.innerHTML = ICONS[name];
          btn.addEventListener('click', () => step(gallery, dir));
          wrapper.appendChild(btn);
        });
      }
    });
  };

  /* whole image opens the lightbox on desktop */
  document.addEventListener('click', (event) => {
    if (!window.matchMedia('(min-width: 1000px)').matches) return;
    const img = event.target.closest('.product-gallery__carousel-item[data-media-type="image"] .product-gallery__image');
    if (!img) return;
    const gallery = img.closest('.product-gallery');
    if (gallery) openLightbox(gallery);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance);
  } else {
    enhance();
  }
  /* rAF-throttled: unthrottled full-document observers run continuously in
     the theme editor (its bridge mutates the DOM nonstop) and starve the
     admin UI sharing the tab's CPU. */
  let enhanceScheduled = false;
  new MutationObserver(() => {
    if (enhanceScheduled) return;
    enhanceScheduled = true;
    requestAnimationFrame(() => { enhanceScheduled = false; enhance(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();

/* ------------------------------------------------------------------
   Search popup: close on scroll (desktop). The theme freezes the page
   while the popup is open; custom.css re-enables scrolling and this
   dismisses the popup the moment the visitor scrolls.
   ------------------------------------------------------------------ */
(() => {
  let last = 0;
  window.addEventListener('scroll', () => {
    const now = Date.now();
    if (now - last < 100 || !window.matchMedia('(min-width: 1000px)').matches) return;
    last = now;
    const wrapper = document.querySelector('.header__search-bar-wrapper.is-fixed');
    if (!wrapper) return;
    wrapper.classList.remove('is-fixed');
    document.documentElement.classList.remove('is-locked');
    wrapper.querySelector('.search-bar')?.classList.remove('is-expanded');
    wrapper.querySelector('.search-bar__results')?.setAttribute('aria-hidden', 'true');
    wrapper.querySelector('.search-bar__menu-wrapper')?.setAttribute('aria-hidden', 'true');
    wrapper.querySelector('[name="q"]')?.blur();
  }, { passive: true });
})();

/* ------------------------------------------------------------------
   Cart popup pointer arrow: align to the cart icon dynamically (the
   static offset drifted at some viewport widths). No-op in side-panel
   mode, where the arrow is hidden.
   ------------------------------------------------------------------ */
(() => {
  const align = () => {
    if (!window.matchMedia('(min-width: 1000px)').matches) return;
    const mc = document.getElementById('mini-cart');
    const tri = mc && mc.querySelector('.icon--nav-triangle-borderless');
    const icon = document.querySelector('.header__action-item--cart .header__cart-icon');
    if (!mc || !tri || !icon || getComputedStyle(tri).display === 'none') return;
    const iconCenter = icon.getBoundingClientRect().left + icon.getBoundingClientRect().width / 2;
    const panelRight = mc.getBoundingClientRect().right;
    tri.style.right = Math.max(4, Math.round(panelRight - iconCenter - 9)) + 'px';
  };

  const mc = document.getElementById('mini-cart');
  if (mc) {
    new MutationObserver(() => {
      if (mc.getAttribute('aria-hidden') === 'false') align();
    }).observe(mc, { attributes: true, attributeFilter: ['aria-hidden'] });
  }
  window.addEventListener('resize', align, { passive: true });
})();

/* ------------------------------------------------------------------
   Protection plans (snippets/protection-plans.liquid):
   - re-pick the tier for each offered plan length on variant change
   - add the selected plan alongside the product on Add to Cart, with
     visible "Covers" + hidden _covers_variant properties
   ------------------------------------------------------------------ */
(() => {
  const money = (cents) => (typeof Shopify !== 'undefined' && Shopify.formatMoney)
    ? Shopify.formatMoney(cents)
    : '$' + (cents / 100).toFixed(2);

  const tiersFor = (box) => {
    try { return JSON.parse(box.querySelector('[data-pp-tiers]').textContent); }
    catch (e) { return null; }
  };

  const repick = (box, variantPrice) => {
    const data = tiersFor(box);
    if (!data) return;
    box.querySelectorAll('.pp__row').forEach((row) => {
      const input = row.querySelector('.pp__radio');
      if (!input || input.value === '') return;
      const yearMatch = row.querySelector('.pp__label').textContent.match(/^(\d+)-/);
      if (!yearMatch) return;
      const tiers = data.tiers[yearMatch[1]] || [];
      const tier = tiers.find((t) => t.max > variantPrice);
      if (tier) {
        row.hidden = false;
        input.value = tier.vid;
        input.setAttribute('data-plan-title', tier.title);
        input.setAttribute('data-plan-price', tier.price);
        row.querySelector('.pp__price').textContent = money(tier.price);
      } else {
        row.hidden = true;
        if (input.checked) box.querySelector('.pp__radio[value=""]').checked = true;
      }
    });
  };

  document.addEventListener('variant:changed', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!variant) return;
    document.querySelectorAll('[data-protection]').forEach((box) => repick(box, variant.price));
  });

  /* attach the selected plan after the theme confirms the main product was
     added. The theme's product:added carries the originating button, which
     scopes us to the main product section (and excludes quick-view + our
     own FBT dispatches, which have no button). */
  document.documentElement.addEventListener('product:added', (event) => {
    const d = event.detail || {};
    if (!d.productAdded || !d.button || !d.button.closest) return;
    const section = d.button.closest('[data-section-type="product"]');
    if (!section) return;
    const box = section.querySelector('[data-protection]');
    if (!box) return;
    const chosen = box.querySelector('.pp__radio:checked');
    if (!chosen || !chosen.value) return;
    const data = tiersFor(box) || {};
    const form = d.button.closest('form');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{
          id: parseInt(chosen.value, 10),
          quantity: 1,
          properties: {
            'Covers': (data.coversSku ? data.coversSku + ' — ' : '') + (data.covers || ''),
            '_covers_variant': String((form && form.querySelector('[name="id"]') || {}).value || '')
          }
        }]
      })
    }).then((r) => {
      if (r.ok) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }).catch(() => { /* plan add is best-effort; the product itself was added */ });
  });
})();


/* ============================================================
   Protection plan offers in the cart (mini-cart drawer, side
   panel and cart page). The [data-pp-cart-select] dropdowns are
   server-rendered per line item by protection-plans-cart-offer;
   on pick we add the plan with the same Covers/_covers_variant
   properties as the PDP attach, then cart:refresh re-renders the
   cart — the offer disappears because the item is now covered.
   Delegated listener so it survives every cart re-render.
   ============================================================ */
(() => {
  document.addEventListener('change', (event) => {
    const sel = event.target.closest && event.target.closest('[data-pp-cart-select]');
    if (!sel || !sel.value) return;
    const planId = parseInt(sel.value, 10);
    if (!planId) return;
    sel.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{
          id: planId,
          quantity: 1,
          properties: {
            'Covers': (sel.getAttribute('data-covers-sku') ? sel.getAttribute('data-covers-sku') + ' — ' : '') + (sel.getAttribute('data-covers-title') || ''),
            '_covers_variant': sel.getAttribute('data-covers-variant') || ''
          }
        }]
      })
    }).then((r) => {
      if (r.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } else {
        sel.disabled = false;
        sel.value = '';
      }
    }).catch(() => {
      sel.disabled = false;
      sel.value = '';
    });
  });
})();


/* PDP purchase rail: recap Add to Cart proxies the main buy button so
   variant selection / quantity / protection attach stay intact. */
(() => {
  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-pdp-recap-atc]');
    if (!btn) return;
    const main = document.querySelector('.pdp-hero .product-form__add-button');
    if (main) {
      main.click();
    }
  });
})();


/* PDP: stars/review-count badge scrolls to the Judge.me review widget
   (the theme's #product-reviews anchor doesn't exist on this template). */
(() => {
  document.addEventListener('click', (event) => {
    const badge = event.target.closest && event.target.closest('.product-meta__reviews-badge');
    if (!badge) return;
    /* .jdgm-rev-widg only exists after Judge.me's script initializes the
       widget; freshly swapped markup carries the raw wrapper classes/id */
    const target = document.querySelector('.jdgm-rev-widg') || document.getElementById('judgeme_product_reviews') || document.querySelector('.jdgm-review-widget');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();


/* Expert card: Live Chat opens Tidio (loads via app embed). */
(() => {
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('.open_live_chat');
    if (!link) return;
    event.preventDefault();
    if (window.tidioChatApi) {
      try { window.tidioChatApi.show(); window.tidioChatApi.open(); } catch (e) { /* fall through */ }
    }
  });
})();


/* PDP dialogs (CPS protection popup, shipping details): native <dialog>,
   openers carry data-pdp-dialog-open="<selector>", backdrop click closes. */
(() => {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest && event.target.closest('[data-pdp-dialog-open]');
    if (opener) {
      event.preventDefault();
      const sel = opener.getAttribute('data-pdp-dialog-open');
      const scope = opener.closest('.pp, .product-info__block-item');
      const dlg = (scope && scope.querySelector(sel)) || document.querySelector(sel);
      if (dlg && typeof dlg.showModal === 'function' && !dlg.open) dlg.showModal();
      return;
    }
    const closer = event.target.closest && event.target.closest('[data-pdp-dialog-close]');
    if (closer) {
      const d = closer.closest('dialog');
      if (d) d.close();
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.hasAttribute('data-pdp-dialog') && event.target.open) {
      const r = event.target.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) {
        event.target.close();
      }
    }
  });
})();


/* PDP scroll-spy chip bar (Phase C). Chips with no matching target are
   hidden; active chip follows scroll. Re-init after linked-variant swaps
   via window.hodChipbarInit(). */
(() => {
  let bar = null;
  let pairs = [];

  const offset = () => {
    if (!bar) return 0;
    const top = parseFloat(getComputedStyle(bar).top) || 0;
    return bar.getBoundingClientRect().height + top + 12;
  };

  const setActive = () => {
    if (!pairs.length) return;
    const line = offset() + 48;
    let current = null;
    pairs.forEach((p) => {
      if (p.target.getBoundingClientRect().top <= line) current = p;
    });
    pairs.forEach((p) => p.chip.classList.toggle('is-active', p === current));
  };

  const init = () => {
    bar = document.querySelector('[data-pdp-chipbar]');
    pairs = [];
    if (!bar) return;
    bar.querySelectorAll('[data-chip-target]').forEach((chip) => {
      const target = document.querySelector(chip.getAttribute('data-chip-target'));
      if (!target) { chip.hidden = true; return; }
      chip.hidden = false;
      pairs.push({ chip, target });
    });
    bar.hidden = pairs.length === 0;
    setActive();
  };
  window.hodChipbarInit = init;

  document.addEventListener('click', (event) => {
    const chip = event.target.closest && event.target.closest('.pdp-chipbar__chip');
    if (!chip) return;
    const pair = pairs.find((p) => p.chip === chip);
    if (!pair) return;
    /* accordion: close the other cards' open + collapses */
    pairs.forEach((p) => {
      if (p === pair) return;
      const openBtn = p.target.querySelector('.card__collapsible-button[aria-expanded="true"]');
      if (openBtn) openBtn.click();
    });
    /* expand the target: + collapse cards and the description view-more */
    const collapsed = pair.target.querySelector('.card__collapsible-button[aria-expanded="false"]');
    if (collapsed) collapsed.click();
    const clampedToggle = pair.target.querySelector('.expandable-content:not([data-expanded="true"]) .expandable-content__toggle');
    if (clampedToggle) clampedToggle.click();
    /* let the open/close animations settle before measuring the scroll target */
    setTimeout(() => {
      const y = pair.target.getBoundingClientRect().top + window.scrollY - offset();
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    }, 330);
  });

  /* sticky can't span past the product section, so pin to fixed once the
     bar's natural position scrolls under the header */
  let placeholder = null;
  const headerH = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
  const pin = () => {
    if (!bar) return;
    if (!placeholder || !placeholder.isConnected) {
      document.querySelectorAll('[data-chip-ph]').forEach((p) => p.remove());
      placeholder = document.createElement('div');
      placeholder.setAttribute('data-chip-ph', '');
      bar.parentNode.insertBefore(placeholder, bar);
    }
    const hdr = document.querySelector('#shopify-section-header, .shopify-section--header, header');
    const hdrBottom = hdr ? Math.max(0, hdr.getBoundingClientRect().bottom) : 0;
    const anchorTop = placeholder.getBoundingClientRect().top;
    const fixed = bar.classList.contains('is-fixed');
    if (!fixed && anchorTop <= hdrBottom) {
      placeholder.style.height = bar.offsetHeight + 'px';
      bar.classList.add('is-fixed');
    } else if (fixed && anchorTop > hdrBottom) {
      bar.classList.remove('is-fixed');
      placeholder.style.height = '0px';
      bar.style.top = '';
    }
    if (bar.classList.contains('is-fixed')) {
      bar.style.top = Math.round(hdrBottom) + 'px';
      /* hug the reading column so the bar never covers the purchase rail */
      const col = document.querySelector('.pdp-lower .product-block-list__wrapper');
      if (col) {
        const cr = col.getBoundingClientRect();
        bar.style.left = Math.round(cr.left) + 'px';
        bar.style.width = Math.round(cr.width) + 'px';
        bar.style.right = 'auto';
      }
    } else {
      bar.style.left = '';
      bar.style.width = '';
      bar.style.right = '';
    }
  };

  let raf = null;
  window.addEventListener('scroll', () => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = null; setActive(); });
  }, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  /* re-run after full load: scroll restoration and late images move the
     bar's natural position (prevents the visible jump on load) */
  window.addEventListener('load', () => { setActive(); });
})();


/* Description view-more: replace the theme's toggle handler for #pdp-desc.
   The theme leaves a stale inline margin on close and fires a competing
   smooth-scroll mid-animation (Stas: "glitches as it auto moves up"). */
document.addEventListener('click', (event) => {
  const btn = event.target.closest && event.target.closest('.pdp-desc-toggle');
  if (!btn) return;
  event.stopPropagation();
  event.preventDefault();
  const box = btn.closest('.expandable-content');
  const txt = btn.querySelector('.pdp-desc-toggle__text');
  if (box.getAttribute('data-expanded') === 'true') {
    box.setAttribute('data-expanded', 'false');
    if (txt) txt.innerHTML = txt.getAttribute('data-view-more');
    const card = box.closest('.card');
    if (card && card.getBoundingClientRect().top < 0) {
      const y = card.getBoundingClientRect().top + window.scrollY - 130;
      window.scrollTo({ top: Math.max(0, y) });
    }
  } else {
    box.setAttribute('data-expanded', 'true');
    if (txt) txt.innerHTML = txt.getAttribute('data-view-less');
  }
}, true);


/* hide the description view-more when the content already fits */
window.addEventListener('load', () => {
  document.querySelectorAll('#pdp-desc .expandable-content, #pdp-specs .expandable-content, #pdp-manuals .expandable-content, #pdp-tab4 .expandable-content').forEach((box) => {
    const btn = box.querySelector('.pdp-desc-toggle');
    if (btn && box.getAttribute('data-expanded') !== 'true' && box.scrollHeight <= box.clientHeight + 40) {
      btn.hidden = true;
      box.classList.add('pdp-desc--fits');
    }
  });
});


/* brand band: Read reviews scrolls to the Judge.me widget */
document.addEventListener('click', (event) => {
  const btn = event.target.closest && event.target.closest('[data-scroll-reviews]');
  if (!btn) return;
  const target = document.querySelector('.jdgm-rev-widg') || document.getElementById('judgeme_product_reviews') || document.querySelector('.jdgm-review-widget');
  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});


/* FBT quick view: the theme's open-quick-view delegate is bound inside its
   own components only, so fetch the quick-view fragment ourselves. */
window.hodLoadQuickView = (modal, url) => {
  modal.classList.add('is-loading');
  fetch(url, { credentials: 'same-origin' })
    .then((r) => { if (!r.ok) throw new Error(r.status); return r.text(); })
    .then((content) => {
      const doc = new DOMParser().parseFromString(content, 'text/html');
      let inner = doc.querySelector('[data-section-type="product-quick-view"]');
      if (!inner) {
        /* the theme ships the quick-view section inside a <template> */
        for (const tpl of doc.querySelectorAll('template')) {
          inner = tpl.content.querySelector('[data-section-type="product-quick-view"]');
          if (inner) break;
        }
      }
      if (!inner) throw new Error('no quick view section');
      modal.querySelector('.modal__inner').replaceChildren(document.importNode(inner, true));
      modal.classList.remove('is-loading');
    })
    .catch(() => { window.location.href = url; });
};

document.addEventListener('click', (event) => {
  const trigger = event.target.closest && event.target.closest('.fbt__qv');
  if (!trigger) return;
  const modal = document.getElementById(trigger.getAttribute('aria-controls'));
  const url = trigger.getAttribute('data-product-url');
  if (!modal || !url) return;
  window.hodLoadQuickView(modal, url);
});


/* accessories card: outline Add button adds the variant, refreshes mini-cart */
document.addEventListener('click', (event) => {
  const btn = event.target.closest && event.target.closest('[data-acc-add]');
  if (!btn || btn.disabled) return;
  btn.disabled = true;
  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items: [{ id: parseInt(btn.getAttribute('data-variant-id'), 10), quantity: 1, properties: Object.assign(window.hodStockProps(btn), { _added_via: 'PDP Accessories' }) }] })
  }).then((r) => {
    if (!r.ok) throw new Error(r.status);
    /* straight-edge check (theme 'check' icon), not the cursive ✓ glyph (Stas 8/28) */
    btn.innerHTML = (btn.getAttribute('data-label-added') || 'Added') + ' <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true" style="vertical-align:-1px;margin-left:2px"><path fill="currentColor" d="M9 20l-7-7 3-3 4 4L19 4l3 3z"/></svg>';
    document.documentElement.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { quantity: 1 } }));
    setTimeout(() => { btn.textContent = 'Add'; btn.disabled = false; }, 2200);
  }).catch(() => { btn.disabled = false; });
});


/* reviews card: title becomes '<Vendor> Reviews' */
window.hodReviewsTitle = () => {
  const wrap = document.querySelector('.product-block-list__item--reviews[data-vendor]');
  if (!wrap) return;
  const vendor = wrap.getAttribute('data-vendor');
  const t = wrap.querySelector('.jdgm-rev-widg__title');
  if (!t || !vendor) return;
  t.textContent = vendor + ' Reviews';
  if (!wrap.querySelector('.pdp-reviews-sub')) {
    const sub = document.createElement('div');
    sub.className = 'pdp-reviews-sub';
    sub.textContent = 'Verified reviews from across all ' + vendor + ' products \u00b7 collected by Judge.me';
    t.insertAdjacentElement('afterend', sub);
  }
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.hodReviewsTitle);
} else {
  window.hodReviewsTitle();
}
window.addEventListener('load', window.hodReviewsTitle);


/* Phase E: mobile sticky buy bar — shows as soon as the real Add to Cart
   scrolls off the top of the screen (Stas rev 8.1; was FBT-based). Also
   lifts the Tidio launcher above the bar while it's shown: Tidio's shadow
   root is open, so we inject one rule keyed off a host attribute — custom
   properties inherit across the shadow boundary, so the offset follows
   the bar's real height. Price follows variant changes; ATC proxies the
   main button. */
(() => {
  const bar = document.querySelector('[data-pdp-buybar]');
  if (!bar) return;
  const trigger = () =>
    document.querySelector('.pdp-hero .product-form__add-button') ||
    document.querySelector('.pdp-hero .product-form__payment-container') ||
    document.querySelector('.pdp-hero');
  const liftChat = (show) => {
    const host = document.querySelector('#tidio-chat');
    if (!host || !host.shadowRoot) return;
    if (!host.shadowRoot.querySelector('[data-hod-lift-style]')) {
      const st = document.createElement('style');
      st.setAttribute('data-hod-lift-style', '');
      /* Tidio also sets `top`, which wins over `bottom` on positioned
         boxes — force it back to auto so the lift takes effect */
      st.textContent = ':host([data-hod-lift]) #tidio-chat-root #button{top:auto !important;bottom:var(--hod-lift,72px) !important}' +
        ':host([data-hod-lift]) #tidio-chat-root .widgetLabel{top:auto !important;bottom:calc(var(--hod-lift,72px) + 7px) !important}';
      host.shadowRoot.appendChild(st);
    }
    if (show) host.style.setProperty('--hod-lift', (bar.offsetHeight + 10) + 'px');
    host.toggleAttribute('data-hod-lift', show);
  };
  let raf = null;
  const update = () => {
    const t = trigger();
    /* bottom < 0 = the button scrolled past the top; a below-the-fold
       button on page load keeps the bar hidden */
    const show = window.innerWidth < 1000 && t && t.getBoundingClientRect().bottom < 0;
    bar.hidden = !show;
    liftChat(show);
  };
  window.addEventListener('scroll', () => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = null; update(); });
  }, { passive: true });
  window.addEventListener('resize', update);
  document.addEventListener('variant:changed', (event) => {
    const v = event.detail && event.detail.variant;
    const el = bar.querySelector('[data-buybar-price]');
    if (v && el) el.textContent = '$' + (v.price / 100).toLocaleString('en-US', { minimumFractionDigits: 2 });
  });
  update();
})();


/* reviews polish that must run on the POST-parse jdgm DOM: split the
   average into big number + small text, force gallery images to load */
window.hodReviewsPolish = () => {
  const w = document.getElementById('judgeme_product_reviews');
  if (!w) return;
  const avg = w.querySelector('.jdgm-rev-widg__summary-average');
  if (avg && !avg.getAttribute('data-split')) {
    const m = avg.textContent.trim().match(/^([\d.]+)\s*(.*)$/);
    if (m) {
      avg.textContent = '';
      const b = document.createElement('b');
      b.textContent = m[1];
      avg.appendChild(b);
      avg.appendChild(document.createTextNode(' ' + (m[2] || 'out of 5')));
      avg.setAttribute('data-split', '1');
    }
  }
  if (typeof window.hodFractionalStars === 'function') window.hodFractionalStars();
  w.querySelectorAll('.jdgm-histogram__row').forEach((row) => {
    const cell = row.querySelector('.jdgm-histogram__star');
    if (cell && !cell.getAttribute('data-simple')) {
      cell.textContent = (row.getAttribute('data-rating') || '') + ' \u2605';
      cell.setAttribute('data-simple', '1');
    }
  });
  w.querySelectorAll('.jdgm-gallery__thumbnail[data-src]:not([src])').forEach((img) => {
    img.src = img.getAttribute('data-src');
    const wrap = img.closest('.jdgm-gallery__thumbnail-wrapper');
    if (wrap) wrap.classList.remove('jdgm--loading');
  });
  /* jdgm's late-injected stylesheet outguns ours on the imgs — inline wins */
  w.querySelectorAll('.jdgm-gallery__thumbnail').forEach((img) => {
    img.style.setProperty('width', '100%', 'important');
    img.style.setProperty('height', '100%', 'important');
    img.style.setProperty('object-fit', 'cover', 'important');
    img.style.setProperty('max-width', 'none', 'important');
  });
};
(() => {
  const run = window.hodReviewsPolish;
  let obs = null;
  let observed = null;
  /* linked-variant swaps replace the widget node — re-attach on demand */
  window.hodReviewsObserve = () => {
    const w = document.getElementById('judgeme_product_reviews');
    if (!w || !window.MutationObserver || w === observed) return;
    if (obs) obs.disconnect();
    observed = w;
    let t = null;
    obs = new MutationObserver(() => { clearTimeout(t); t = setTimeout(run, 150); });
    obs.observe(w, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  window.addEventListener('load', run);
  window.hodReviewsObserve();
  window.addEventListener('load', window.hodReviewsObserve);
})();


/* flickity needs a resize nudge after our card margins change slide widths */
window.addEventListener('load', () => {
  setTimeout(() => {
    document.querySelectorAll('.flickity-enabled').forEach((el) => {
      try {
        if (window.Flickity && Flickity.data(el)) Flickity.data(el).resize();
      } catch (e) { /* non-critical */ }
    });
  }, 800);
});


/* fractional star fills: 4.86 renders as 97.2%-filled star row instead of
   five solid stars — applied to jdgm preview badges (title row + product
   cards) and the reviews-summary stars */
window.hodFractionalStars = () => {
  const build = (rating) => {
    const el = document.createElement('span');
    el.className = 'hod-stars';
    el.setAttribute('role', 'img');
    el.setAttribute('aria-label', rating + ' out of 5 stars');
    for (let i = 1; i <= 5; i++) {
      const s = document.createElement('span');
      if (rating >= i) {
        s.className = 'hod-star';
        s.textContent = '\u2605';
      } else if (rating > i - 1) {
        let pct = Math.round(((rating - (i - 1)) * 100) / 5) * 5;
        if (pct > 80) pct = 80;
        s.className = 'hod-star hod-star--part';
        s.innerHTML = '<span class="hod-star__fill" style="width:' + pct + '%">\u2605</span>\u2605';
      } else {
        s.className = 'hod-star hod-star--empty';
        s.textContent = '\u2605';
      }
      el.appendChild(s);
    }
    return el;
  };
  document.querySelectorAll('.rating__stars:not([data-hod-stars])').forEach((el) => {
    const m = (el.getAttribute('aria-label') || '').match(/([\d.]+)/);
    if (!m) return;
    const rating = parseFloat(m[1]);
    el.replaceChildren(build(rating));
    const wrap = el.closest('.rating') || el.parentElement;
    if (wrap && !wrap.querySelector('.hod-rating-num')) {
      const num = document.createElement('span');
      num.className = 'hod-rating-num';
      num.textContent = rating.toFixed(2);
      wrap.insertBefore(num, el);
    }
    const badge = el.closest('.product-meta__reviews-badge');
    if (badge) {
      const cap = [...badge.querySelectorAll('span, div')].find((c) => /\d[\d,]*\s*reviews/i.test(c.textContent) && c.children.length === 0);
      if (cap) {
        const n = (cap.textContent.match(/[\d,]+/) || [])[0];
        if (n) cap.textContent = '(' + n + ')';
      }
    }
    el.setAttribute('data-hod-stars', '1');
  });
  document.querySelectorAll('.jdgm-prev-badge:not([data-hod-stars])').forEach((b) => {
    const r = parseFloat(b.getAttribute('data-average-rating'));
    const stars = b.querySelector('.jdgm-prev-badge__stars');
    if (!r || !stars) return;
    stars.replaceChildren(build(r));
    /* collection cards use the compact form: "4.86 ★" (number + one
       star; CSS hides stars 2-5) — Stas 8.2. PDP carousels (You may
       also like / Recently viewed) get the number too — 8/20, cards
       must match the collection design. */
    const wantsNum = document.body.classList.contains('template-collection') ||
      b.closest('[id*="product-recommendations"], [id*="recently-viewed"]');
    if (wantsNum && !b.querySelector('.hod-rating-num')) {
      const num = document.createElement('span');
      num.className = 'hod-rating-num';
      num.textContent = r.toFixed(2).replace(/0$/, '');
      b.insertBefore(num, stars);
    }
    b.setAttribute('data-hod-stars', '1');
  });
  const sum = document.querySelector('#judgeme_product_reviews .jdgm-rev-widg__summary-stars:not([data-hod-stars])');
  if (sum) {
    const avg = sum.querySelector('.jdgm-rev-widg__summary-average');
    const r = avg ? parseFloat(avg.textContent) : NaN;
    if (r) {
      sum.querySelectorAll('.jdgm-star').forEach((s) => s.remove());
      sum.appendChild(build(r));
      sum.setAttribute('data-hod-stars', '1');
    }
  }
};
(() => {
  const run = window.hodFractionalStars;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
  window.addEventListener('load', run);
  window.setTimeout(run, 1500);
})();





/* ============================================================
   Phase F.2 — Line set panel (HVAC).
   Rendered by snippets/pdp-line-sets.liquid ([data-line-sets]).
   - keeps the panel state in sync (count pill, badge, hint,
     subtotal, amber/navy flip)
   - guards Add to Cart in CAPTURE phase (survives the theme's
     payment-container rerenders): incomplete zones block the add,
     flag the panel, scroll back and show a warning row. The button
     itself is never visually disabled (design intent in the mock).
   - after the main product is added (main ATC or FBT Add Selected),
     attaches the chosen line sets / heat kit as separate cart line
     items with Stock/ETA properties, like the legacy system did.
   All listeners are delegated so linked-variant section swaps and
   variant rerenders need no re-init.
   ============================================================ */
(() => {
  const panel = () => document.querySelector('[data-line-sets]');
  const zonesOf = (box) => Array.prototype.slice.call(box.querySelectorAll('[data-ls-zone]'));
  const missing = (box) => zonesOf(box).filter((s) => !s.value).length;

  const fmt = (cents) => {
    try {
      if (window.Shopify && Shopify.formatMoney && window.theme && window.theme.moneyFormat) {
        return Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
    } catch (e) { /* fall through */ }
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const warnRow = () => document.querySelector('.ls-warn');

  /* per-zone stock status under the select — appears only once a length is
     picked; reuses the product-card stock classes so color/text match */
  const updateStockRow = (sel) => {
    let row = sel.nextElementSibling;
    if (!(row && row.hasAttribute && row.hasAttribute('data-ls-stock'))) {
      const zone = sel.closest('.ls-zone');
      row = zone ? zone.querySelector('[data-ls-stock]') : null;
    }
    if (!row) return;
    const opt = sel.value ? (sel.selectedOptions && sel.selectedOptions[0]) : null;
    const label = opt && opt.getAttribute('data-stock-label');
    if (!label) { row.hidden = true; row.innerHTML = ''; return; }
    const eta = (opt.getAttribute('data-stock-text') || '').trim();
    let state = 'in';
    if (/Out/i.test(label) || eta === 'Discontinued') state = 'oos';
    else if (/Backorder|Pre|order/i.test(label)) state = 'bo';
    /* status label only — the full ETA line wraps badly in the narrow zone
       column (mockup shows just the label); ETA still rides the cart props */
    const short = label.split('|')[0].trim();
    row.innerHTML = '<div class="hod-card-stock hod-card-stock--' + state + '"><i></i><b>' +
      short.replace(/</g, '&lt;') + '</b></div>';
    row.hidden = false;
  };

  const sync = (box) => {
    const zones = zonesOf(box);
    zones.forEach((s) => s.classList.toggle('is-empty', !s.value));
    box.querySelectorAll('.ls-select').forEach(updateStockRow);
    const miss = zones.filter((s) => !s.value).length;
    const done = miss === 0;
    box.classList.toggle('is-done', done);
    if (done) box.classList.remove('is-error');
    const badge = box.querySelector('[data-ls-badge]');
    if (badge) badge.textContent = done ? '✓' : '!';
    const count = box.querySelector('[data-ls-count]');
    if (count) count.textContent = (zones.length - miss) + ' of ' + zones.length;
    const hint = box.querySelector('[data-ls-hint]');
    if (hint) hint.textContent = hint.getAttribute(done ? 'data-hint-done' : 'data-hint-todo');

    let total = 0;
    let picked = 0;
    box.querySelectorAll('.ls-select').forEach((s) => {
      if (!s.value) return;
      const opt = s.selectedOptions && s.selectedOptions[0];
      total += opt ? parseInt(opt.getAttribute('data-price') || '0', 10) : 0;
      picked += 1;
    });
    const foot = box.querySelector('[data-ls-footer]');
    if (foot) {
      foot.hidden = picked === 0;
      const sub = box.querySelector('[data-ls-subtotal]');
      if (sub) sub.textContent = fmt(total);
    }

    const warn = warnRow();
    if (warn) {
      if (done) {
        warn.remove();
      } else {
        const rem = warn.querySelector('[data-ls-remaining]');
        if (rem) rem.innerHTML = '<b>' + miss + ' remaining</b>';
      }
    }

    /* H.4: mirror the panel state into the rail recap card */
    if (zones.length > 0) {
      let row = document.querySelector('[data-ls-recap-row]');
      if (!row) {
        const host = document.querySelector('[data-pdp-recap-atc]');
        if (host && host.parentElement) {
          row = document.createElement('div');
          row.setAttribute('data-ls-recap-row', '');
          row.className = 'ls-recap-row';
          host.parentElement.insertBefore(row, host);
        }
      }
      if (row) {
        row.innerHTML = done
          ? '<span class="ls-recap-badge is-done">\u2713</span> Line sets chosen (' + zones.length + ' of ' + zones.length + ')'
          : '<span class="ls-recap-badge">!</span> Line sets: ' + (zones.length - miss) + ' of ' + zones.length + ' chosen';
      }
    }

    applyHeadline(box);
  };

  /* ---- headline price follows the selections (Stas 8.1) ----
     new price = variant base (data-base-price, refreshed on
     variant:changed) + every selected option's data-price; strike and
     SAVE badge get the same treatment via data-comp. Only the money
     text node is replaced so the visually-hidden a11y spans survive.
     The theme rerenders the price block server-side on variant change,
     so a MutationObserver re-applies after each replacement — writes
     are no-ops when the text already matches, which breaks the loop. */
  const setMoneyText = (el, text) => {
    if (!el) return;
    let node = null;
    el.childNodes.forEach((n) => { if (n.nodeType === 3 && n.nodeValue.trim() !== '') node = n; });
    if (node) {
      if (node.nodeValue.trim() !== text) node.nodeValue = text;
    } else if (el.textContent.trim() !== text) {
      el.textContent = text;
    }
  };

  const applyHeadline = (box) => {
    const base = parseInt(box.getAttribute('data-base-price') || '0', 10);
    if (!base) return;
    const baseComp = parseInt(box.getAttribute('data-base-compare') || '0', 10) || base;
    let addP = 0;
    let addC = 0;
    box.querySelectorAll('.ls-select').forEach((s) => {
      if (!s.value) return;
      const opt = s.selectedOptions && s.selectedOptions[0];
      if (!opt) return;
      const p = parseInt(opt.getAttribute('data-price') || '0', 10);
      addP += p;
      addC += parseInt(opt.getAttribute('data-comp') || '0', 10) || p;
    });
    const price = base + addP;
    const compare = baseComp + addC;
    setMoneyText(document.querySelector('.pdp-hero .price--highlight'), fmt(price));
    if (compare > price) {
      setMoneyText(document.querySelector('.pdp-hero .price--compare'), fmt(compare));
      const badge = document.querySelector('.pdp-hero .pdp-save-badge');
      if (badge) {
        /* floor division matches the Liquid badge math in product-price */
        const pct = 'SAVE ' + Math.floor(((compare - price) * 100) / compare) + '%';
        if (badge.textContent.trim() !== pct) badge.textContent = pct;
      }
    }
    const buybar = document.querySelector('[data-buybar-price]');
    if (buybar && buybar.textContent.trim() !== fmt(price)) buybar.textContent = fmt(price);
  };

  document.addEventListener('variant:changed', (event) => {
    const v = event.detail && event.detail.variant;
    const box = panel();
    if (!v || !box) return;
    box.setAttribute('data-base-price', String(v.price));
    box.setAttribute('data-base-compare', String(v.compare_at_price || 0));
    applyHeadline(box);
  });

  /* re-apply after the theme's async price-block rerender */
  const hero = document.querySelector('.pdp-hero');
  if (hero && window.MutationObserver) {
    let pending = null;
    new MutationObserver(() => {
      const box = panel();
      if (!box) return;
      if (pending) return;
      pending = setTimeout(() => { pending = null; applyHeadline(box); }, 60);
    }).observe(hero, { childList: true, subtree: true });
  }

  document.addEventListener('change', (event) => {
    const sel = event.target.closest && event.target.closest('.ls-select');
    if (!sel) return;
    const box = sel.closest('[data-line-sets]');
    if (box) sync(box);
  });

  /* ---- ATC guard ---- */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest &&
      event.target.closest('.pdp-hero .product-form__add-button, [data-fbt-add]');
    if (!btn) return;
    const box = panel();
    if (!box) return;
    const miss = missing(box);
    if (!miss) return;

    event.preventDefault();
    event.stopPropagation();
    box.classList.add('is-error');

    let warn = warnRow();
    if (!warn) {
      warn = document.createElement('div');
      warn.className = 'ls-warn';
      warn.innerHTML = '<span class="ls-warn-badge">!</span><span>Choose a line set length for each air handler — <span data-ls-remaining></span></span>';
      const anchor = document.querySelector('.pdp-hero .product-info__block-item--buy-buttons') || btn;
      anchor.insertAdjacentElement('afterend', warn);
    }
    const rem = warn.querySelector('[data-ls-remaining]');
    if (rem) rem.innerHTML = '<b>' + miss + ' remaining</b>';

    const top = box.getBoundingClientRect().top + window.scrollY - 90;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }, true);

  /* ---- attach line sets after the main product lands in the cart ----
     Main ATC: the theme's product:added carries the originating button
     (same scoping as the protection-plan attach). FBT Add Selected marks
     its own dispatch with fbtAdd (its items include the main product).
     Accessory adds carry neither, so they never attach line sets. */
  document.documentElement.addEventListener('product:added', (event) => {
    const d = event.detail || {};
    const viaMain = d.productAdded && d.button && d.button.closest &&
      d.button.closest('[data-section-type="product"]');
    if (!viaMain && d.fbtAdd !== true) return;
    const box = panel();
    if (!box || missing(box)) return;

    const items = [];
    box.querySelectorAll('.ls-select').forEach((s) => {
      if (!s.value) return;
      const opt = s.selectedOptions && s.selectedOptions[0];
      const properties = {};
      const stock = opt && opt.getAttribute('data-stock-label');
      const eta = opt && opt.getAttribute('data-stock-text');
      if (stock) properties['Stock'] = stock;
      if (eta) properties['ETA'] = eta;
      items.push({ id: parseInt(s.value, 10), quantity: 1, properties: properties });
    });
    if (!items.length) return;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    }).then((r) => {
      if (r.ok) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }).catch(() => { /* best-effort: the system itself was added */ });
  });
})();


/* ============================================================
   H.2 — fractional stars survive AJAX collection filtering.
   CollectionSection swaps .collection__dynamic-part innerHTML on
   every filter/sort/page change; the load-time hodFractionalStars
   runners never see the new cards. The :not([data-hod-stars])
   guards inside hodFractionalStars make re-runs cheap.
   ============================================================ */
(() => {
  const host = document.querySelector('[data-section-type="collection"], [data-section-type="search"]');
  if (!host || !window.MutationObserver) return;
  let pending = null;
  new MutationObserver(() => {
    if (pending) return;
    pending = setTimeout(() => {
      pending = null;
      if (typeof window.hodFractionalStars === 'function') window.hodFractionalStars();
    }, 120);
  }).observe(host, { childList: true, subtree: true });
})();


/* ============================================================
   G.1 branded collections — quick-nav / shop-by-style tiles
   toggle sidebar facets. We find the matching facet checkbox by
   its LABEL TEXT inside the desktop filter form and click it;
   theme.js's [name^=filter.] change listener then AJAX-reloads
   the grid. Single-select: any sibling tile's active value in
   the same rail is unchecked first. Optimistic is-active class
   (server truth returns on full page load).
   ============================================================ */
(() => {
  const findFacetInput = (value) => {
    const form = document.querySelector('#desktop-filters-form');
    if (!form || !value) return null;
    const want = value.trim().toLowerCase();
    /* Swatch-rendered facets (custom.finish) put the color circle in the
       first span, so check every non-decoration span — and when the same
       label text exists in two facets (e.g. "Stainless Steel" in both
       Finish and the Material taxonomy), prefer the Finish facet. */
    let fallback = null;
    const labels = form.querySelectorAll('.collection__filter-checkbox label, .collection__filter-swatch-item label');
    for (const label of labels) {
      let hit = false;
      for (const span of label.querySelectorAll('span')) {
        if (span.classList.contains('collection__filter-count') || span.classList.contains('collection__filter-swatch-color')) continue;
        if (span.textContent.trim().toLowerCase() === want) { hit = true; break; }
      }
      if (!hit) continue;
      const input = form.querySelector('#' + CSS.escape(label.getAttribute('for')));
      if (!input) continue;
      if (input.name === 'filter.p.m.custom.finish') return input;
      if (!fallback) fallback = input;
    }
    return fallback;
  };

  document.addEventListener('click', (event) => {
    const tile = event.target.closest && event.target.closest('[data-hod-filter-tile]');
    if (!tile) return;
    const value = tile.getAttribute('data-filter-value');
    const input = findFacetInput(value);
    if (!input) return;

    const rail = tile.closest('.hod-btiles');
    const willActivate = !input.checked;

    /* single-select within the rail: uncheck other active tiles' facets */
    if (rail && willActivate) {
      rail.querySelectorAll('[data-hod-filter-tile].is-active').forEach((other) => {
        if (other === tile) return;
        const otherInput = findFacetInput(other.getAttribute('data-filter-value'));
        if (otherInput && otherInput.checked) {
          otherInput.checked = false;
        }
        other.classList.remove('is-active');
      });
    }

    input.checked = willActivate;
    tile.classList.toggle('is-active', willActivate);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();

/* ------------------------------------------------------------------
   H.1 Bundle PDP: cart mechanics for cart_transformation packages.
   The legacy theme posted hidden items[N] inputs with a native form
   submit; 3.9.1 serializes the form to JSON, which mangles those names
   and adds only the parent shell. Instead snippets/pdp-bundle-items
   renders the children as JSON and this interceptor posts them to
   /cart/add.js — children only, parity with the live theme (the parent
   shell never reaches the cart). Dispatches product:added with the
   originating button so the protection-plan attach keeps working.
   ------------------------------------------------------------------ */
(() => {
  let busy = false;

  const addBundle = async (form, button) => {
    const payload = form.querySelector('[data-hod-bundle-items]');
    if (!payload || busy) return;
    let items;
    try { items = JSON.parse(payload.textContent); } catch (e) { return; }
    if (!Array.isArray(items) || !items.length) return;
    busy = true;
    const label = button ? button.textContent : '';
    if (button) { button.disabled = true; button.textContent = 'Adding…'; }
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items })
      });
      if (!res.ok) throw new Error('bundle add failed');
      const added = await res.json();
      const qty = items.reduce((n, it) => n + (it.quantity || 1), 0);
      document.documentElement.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { quantity: qty, productAdded: added, button: button || form.querySelector('.product-form__add-button') }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (button) button.textContent = '✓ Added to Cart';
    } catch (e) {
      if (button) button.textContent = 'Error — try again';
    } finally {
      setTimeout(() => {
        if (button) { button.textContent = label; button.disabled = false; }
        busy = false;
      }, 1600);
    }
  };

  /* capture phase so the theme's own single-item add never fires for
     bundles; click covers the ATC button (and the recap/buybar proxies
     that .click() it), submit covers Enter-key submits */
  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('.product-form__add-button');
    if (!btn || btn.disabled) return;
    const form = btn.closest('form.product-form');
    if (!form || !form.querySelector('[data-hod-bundle-items]')) return;
    event.preventDefault();
    event.stopPropagation();
    addBundle(form, btn);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form.product-form')) return;
    if (!form.querySelector('[data-hod-bundle-items]')) return;
    event.preventDefault();
    event.stopPropagation();
    addBundle(form, form.querySelector('.product-form__add-button'));
  }, true);
})();

/* ------------------------------------------------------------------
   H.1 Bundle PDP: package card — tile picker drives one shared
   Description/Specs/Manuals pane (tab choice persists across pieces,
   per design); buy-column strip thumbs select a piece and scroll to
   the card; arrows page the tile row when it overflows.
   ------------------------------------------------------------------ */
(() => {
  const card = () => document.querySelector('[data-pkg-card]');
  let currentTab = 'desc';

  const applyTab = (pane, key) => {
    if (!pane) return;
    const tabs = pane.querySelectorAll('[data-pkg-tab]');
    if (!tabs.length) return;
    const target = pane.querySelector('[data-pkg-tab="' + key + '"]') || tabs[0];
    const k = target.getAttribute('data-pkg-tab');
    tabs.forEach((t) => { t.classList.toggle('is-active', t === target); t.setAttribute('aria-selected', t === target ? 'true' : 'false'); });
    pane.querySelectorAll('[data-pkg-tabpane]').forEach((p) => {
      p.hidden = p.getAttribute('data-pkg-tabpane') !== k;
    });
  };

  /* K r2 (8/24): Left/Right arrows move between the package-card tabs */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const tab = event.target.closest && event.target.closest('[data-pkg-tab]');
    if (!tab) return;
    const list = tab.closest('[role="tablist"]');
    if (!list) return;
    const tabs = [...list.querySelectorAll('[data-pkg-tab]')];
    const i = tabs.indexOf(tab);
    const next = tabs[(i + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    currentTab = next.getAttribute('data-pkg-tab');
    applyTab(next.closest('[data-pkg-pane]'), currentTab);
    next.focus();
    event.preventDefault();
  });

  const selectTile = (root, idx) => {
    if (!root) return;
    root.querySelectorAll('[data-pkg-tile]').forEach((t) => {
      t.classList.toggle('is-active', t.getAttribute('data-pkg-tile') === String(idx));
    });
    root.querySelectorAll('[data-pkg-pane]').forEach((p) => {
      const on = p.getAttribute('data-pkg-pane') === String(idx);
      p.hidden = !on;
      if (on) applyTab(p, currentTab);
    });
  };

  const updateArrows = () => {
    const root = card();
    if (!root) return;
    const row = root.querySelector('[data-pkg-tilerow]');
    const left = root.querySelector('[data-pkg-arrow="left"]');
    const right = root.querySelector('[data-pkg-arrow="right"]');
    if (!row || !left || !right) return;
    left.hidden = !(row.scrollLeft > 4);
    right.hidden = !(row.scrollLeft + row.clientWidth < row.scrollWidth - 4);
  };

  document.addEventListener('click', (event) => {
    if (!event.target.closest) return;

    const tile = event.target.closest('[data-pkg-tile]');
    if (tile) {
      selectTile(tile.closest('[data-pkg-card]'), tile.getAttribute('data-pkg-tile'));
      return;
    }

    const tab = event.target.closest('[data-pkg-tab]');
    if (tab) {
      currentTab = tab.getAttribute('data-pkg-tab');
      applyTab(tab.closest('[data-pkg-pane]'), currentTab);
      return;
    }

    const jump = event.target.closest('[data-pkg-jump]');
    if (jump) {
      const root = card();
      if (!root) return;
      const idx = jump.getAttribute('data-pkg-jump');
      selectTile(root, idx);
      const y = root.getBoundingClientRect().top + window.scrollY - 58;
      window.scrollTo({ top: y, behavior: 'smooth' });
      /* 8/20 (Stas): the tile row scrolls horizontally on phones — bring
         the selected tile into view too, centered, or the highlight lands
         offscreen and the tap looks like it did nothing. */
      const row = root.querySelector('[data-pkg-tilerow]');
      const tile = row && row.querySelector('[data-pkg-tile="' + idx + '"]');
      if (row && tile) {
        const left = tile.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft;
        /* instant, not smooth — a concurrent smooth window scroll cancels
           element smooth scrolls on touch emulation/some mobiles */
        row.scrollLeft = Math.max(0, left - (row.clientWidth - tile.offsetWidth) / 2);
      }
      return;
    }

    const arrow = event.target.closest('[data-pkg-arrow]');
    if (arrow) {
      const row = arrow.closest('[data-pkg-card]').querySelector('[data-pkg-tilerow]');
      if (row) row.scrollBy({ left: arrow.getAttribute('data-pkg-arrow') === 'left' ? -420 : 420, behavior: 'smooth' });
    }
  });

  const arm = () => {
    const root = card();
    if (!root) return;
    const row = root.querySelector('[data-pkg-tilerow]');
    if (row) row.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    /* image loads reflow the row after the 60ms first pass */
    setTimeout(updateArrows, 60);
    setTimeout(updateArrows, 800);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }
})();

/* T.1b: promo code field on the cart page — Shopify applies codes via
   the /discount/CODE route; redirect back to the cart. */
(() => {
  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-hod-promo-apply]');
    if (!btn) return;
    const input = document.querySelector('[data-hod-promo-input]');
    const code = input && input.value.trim();
    if (!code) return;
    window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart';
  });
})();

/* T.1d: undo-remove toast on the cart page. The theme rerenders the
   cart section after removal, so the toast lives outside it (fixed).
   Bundles can't be re-added as a single line (the parent shell would
   bypass the Cart Transform merge), so they get a link to the PDP. */
(() => {
  if (!document.body.classList.contains('template-cart')) return;
  let bar = null, timer = null, last = null;

  const dismiss = () => { if (bar) bar.remove(); bar = null; if (timer) clearTimeout(timer); };

  const show = () => {
    dismiss();
    bar = document.createElement('div');
    bar.className = 'hod-undo-bar';
    bar.setAttribute('role', 'status');
    const label = document.createElement('span');
    label.textContent = last.title + ' removed from your cart.';
    bar.appendChild(label);
    if (last.bundle > 0) {
      const a = document.createElement('a');
      a.className = 'hod-undo-bar__action';
      a.href = last.url;
      a.textContent = 'View product';
      bar.appendChild(a);
    } else {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hod-undo-bar__action';
      b.textContent = 'Undo';
      b.addEventListener('click', async () => {
        b.disabled = true;
        try {
          const r = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: parseInt(last.variant, 10), quantity: parseInt(last.qty, 10) || 1, properties: last.properties || {} }] })
          });
          if (r.ok) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (e) { /* leave the bar; user can retry from the PDP */ }
        dismiss();
      });
      bar.appendChild(b);
    }
    document.body.appendChild(bar);
    timer = setTimeout(dismiss, 10000);
  };

  document.addEventListener('click', (event) => {
    const rm = event.target.closest && event.target.closest('[data-hod-rm-variant]');
    if (!rm) return;
    let props = null;
    try { props = JSON.parse(rm.getAttribute('data-hod-rm-props') || 'null'); } catch (e) { /* re-add still works without properties */ }
    last = {
      title: rm.getAttribute('data-hod-rm-title') || 'Item',
      variant: rm.getAttribute('data-hod-rm-variant'),
      qty: rm.getAttribute('data-hod-rm-qty'),
      url: rm.getAttribute('data-hod-rm-url'),
      bundle: parseInt(rm.getAttribute('data-hod-rm-bundle') || '0', 10),
      properties: props
    };
    show();
  });
})();

/* CPS cart offer v2: segmented tier buttons (replaces the dropdown).
   Same add semantics as the old [data-pp-cart-select] handler —
   Covers/_covers_variant properties, then cart:refresh (the offer
   disappears because the line is now covered). Delegated. */
(() => {
  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-pp-cart-tier]');
    if (!btn || btn.disabled) return;
    const box = btn.closest('[data-pp-cart-offer]');
    const planId = parseInt(btn.getAttribute('data-plan-id'), 10);
    if (!box || !planId) return;
    box.querySelectorAll('.pp-cart__tier').forEach((b) => { b.disabled = true; b.classList.remove('is-active'); });
    btn.classList.add('is-active');
    box.classList.add('pp-cart--added');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        items: [{
          id: planId,
          quantity: 1,
          properties: {
            'Covers': (box.getAttribute('data-covers-sku') ? box.getAttribute('data-covers-sku') + ' — ' : '') + (box.getAttribute('data-covers-title') || ''),
            '_covers_variant': box.getAttribute('data-covers-variant') || ''
          }
        }]
      })
    }).then((r) => {
      if (r.ok) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } else {
        box.classList.remove('pp-cart--added');
        box.querySelectorAll('.pp-cart__tier').forEach((b) => { b.disabled = false; });
      }
    }).catch(() => {
      box.classList.remove('pp-cart--added');
      box.querySelectorAll('.pp-cart__tier').forEach((b) => { b.disabled = false; });
    });
  });
})();

/* CPS 8/13 (Stas): orphaned-plan cleanup — when the item a protection
   plan covers leaves the cart, the plan goes with it. Runs after every
   cart re-render (capture phase: one theme dispatch doesn't bubble).
   Every plan line carries _covers_variant (set by the PDP attach and
   the cart offer), so an orphan = a CPS line whose covered variant no
   longer has a non-CPS line in the cart. */
(() => {
  let busy = false;
  const sweep = () => {
    if (busy) return;
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => r.json())
      .then((cart) => {
        const productVariants = new Set(
          (cart.items || [])
            .filter((i) => i.vendor !== 'Consumer Priority Service')
            .map((i) => String(i.variant_id))
        );
        const updates = {};
        (cart.items || []).forEach((i) => {
          if (i.vendor !== 'Consumer Priority Service') return;
          const covers = i.properties && i.properties['_covers_variant'];
          if (covers && !productVariants.has(String(covers))) updates[i.key] = 0;
        });
        if (!Object.keys(updates).length) return;
        busy = true;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ updates })
        }).then((r) => {
          busy = false;
          if (r.ok) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }).catch(() => { busy = false; });
      })
      .catch(() => {});
  };
  document.addEventListener('cart:rerendered', sweep, true);
  /* cart-page Remove links are full navigations (no rerender event), so
     sweep once per load too */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  } else {
    sweep();
  }
})();

/* Horizontal-rail hover arrows (Stas): quicknav tiles + PDP card rails.
   Each arrow lights up only when that direction can actually scroll —
   at rest only the right arrow shows; scroll right and the left one
   appears. Touch devices keep native swiping (arrows hidden in CSS). */
(() => {
  const SELECTOR = '.hod-qnav, .hod-btiles--style, .hod-btiles--series, .product-list--scrollable';
  const arm = (rail) => {
    if (rail.dataset.hodArrows) return;
    const host = rail.parentElement;
    if (!host) return;
    rail.dataset.hodArrows = '1';
    host.classList.add('hod-rail-host');
    const mk = (dir) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'hod-rail-arrow hod-rail-arrow--' + dir;
      b.setAttribute('aria-label', dir === 'left' ? 'Scroll left' : 'Scroll right');
      b.innerHTML = dir === 'left'
        ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="#1F4463" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="#1F4463" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      b.addEventListener('click', (e) => {
        e.preventDefault();
        rail.scrollBy({ left: (dir === 'left' ? -1 : 1) * Math.round(rail.clientWidth * 0.8), behavior: 'smooth' });
      });
      host.appendChild(b);
      return b;
    };
    const L = mk('left');
    const R = mk('right');
    const sync = () => {
      const max = rail.scrollWidth - rail.clientWidth - 2;
      const mid = rail.offsetTop + rail.clientHeight / 2;
      L.style.top = mid + 'px'; L.style.transform = 'translateY(-50%)';
      R.style.top = mid + 'px'; R.style.transform = 'translateY(-50%)';
      L.classList.toggle('is-on', rail.scrollLeft > 2);
      R.classList.toggle('is-on', max > 2 && rail.scrollLeft < max);
    };
    rail.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    setTimeout(sync, 300); /* after images/fonts settle */
    sync();
  };
  let railScanTimer = null;
  const scan = () => document.querySelectorAll(SELECTOR).forEach(arm);
  const queueScan = () => { clearTimeout(railScanTimer); railScanTimer = setTimeout(scan, 300); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  new MutationObserver(queueScan).observe(document.documentElement, { childList: true, subtree: true });
})();

/* Sticky Checkout only when the real button is off screen. */
(() => {
  if (!document.body.classList.contains('template-cart')) return;
  const arm = () => {
    const btn = document.querySelector('.cart-summary__checkout');
    if (!btn || !('IntersectionObserver' in window)) return;
    new IntersectionObserver((entries) => {
      document.body.classList.toggle('hod-ckt-visible', entries[0].isIntersecting);
    }, { threshold: 0.4 }).observe(btn);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm); else arm();
})();

/* ============================================================
   H.4 sizing guide: 1b overlaid floor plan (hover/click selects a
   size, legend syncs) + 2a condenser simulator (zone chips autofilled
   server-side from the system's air handlers; totals vs condenser
   capacity with MRCOOL's over-connection allowance).
   ============================================================ */
(() => {
  const activate = (r, key) => {
    if (!r || !key) return;
    r.querySelectorAll('[data-sz-rect]').forEach((x) => x.classList.toggle('is-active', x.getAttribute('data-sz-rect') === key));
    r.querySelectorAll('[data-sz-row]').forEach((x) => x.classList.toggle('is-active', x.getAttribute('data-sz-row') === key));
  };

  const sim = (r) => {
    const panel = r && r.querySelector('[data-sz-panel="cond"]');
    if (!panel) return;
    const condK = parseInt(panel.getAttribute('data-cond-k'), 10) || 0;
    if (!condK) return;
    const onKs = Array.prototype.slice.call(panel.querySelectorAll('[data-sz-zone].is-on'))
      .map((z) => parseInt(z.getAttribute('data-k'), 10) || 0);
    const totalK = onKs.reduce((a, b) => a + b, 0);
    const track = panel.querySelector('.pdp-sizing__barwrap');
    const bar = panel.querySelector('[data-sz-bar]');
    if (!track || !bar) return;
    const pxPerK = ((track.clientWidth - 24) * 0.8) / condK;
    const ramp = ['#1F4463', '#2E5B85', '#42749F', '#5C8DB7', '#6FA0C6', '#86B3D4'];
    bar.innerHTML = '';
    let acc = 0;
    onKs.forEach((k, i) => {
      const seg = document.createElement('span');
      seg.className = 'pdp-sizing__seg';
      const over = acc + k > condK;
      seg.style.width = Math.max(20, Math.round(k * pxPerK)) + 'px';
      seg.style.background = over
        ? 'repeating-linear-gradient(45deg,#F49B33,#F49B33 6px,#FBC98A 6px,#FBC98A 12px)'
        : ramp[i % ramp.length];
      seg.textContent = k + 'K';
      bar.appendChild(seg);
      acc += k;
    });
    const v = panel.querySelector('[data-sz-verdict]');
    if (!v) return;
    if (!totalK) { v.className = 'pdp-sizing__verdict'; v.textContent = 'No zones selected.'; return; }
    const ratio = Math.round((totalK / condK) * 100);
    if (ratio <= 100) {
      v.className = 'pdp-sizing__verdict is-good';
      v.innerHTML = '<b>' + totalK + 'K of ' + condK + 'K connected (' + ratio + '%)</b> — every zone gets full power, even all running at once.';
    } else if (ratio <= 130) {
      v.className = 'pdp-sizing__verdict is-warn';
      v.innerHTML = '<b>' + totalK + 'K connected (' + ratio + '%)</b> — within MRCOOL&rsquo;s allowed range; if every zone peaks at once, rooms share capacity (about ' + Math.round((condK / totalK) * 100) + '% each).';
    } else {
      v.className = 'pdp-sizing__verdict is-bad';
      v.innerHTML = '<b>' + totalK + 'K connected (' + ratio + '%)</b> — over the recommended limit for this condenser. Consider a larger condenser or fewer zones.';
    }
  };

  document.addEventListener('click', (e) => {
    if (!e.target.closest) return;
    const tab = e.target.closest('[data-sz-tab]');
    if (tab) {
      const r = tab.closest('#pdp-sizing');
      r.querySelectorAll('[data-sz-tab]').forEach((b) => b.classList.toggle('is-active', b === tab));
      const key = tab.getAttribute('data-sz-tab');
      r.classList.toggle('is-cond', key === 'cond'); // header's to-scale pill hides on the condenser tab
      r.querySelectorAll('[data-sz-panel]').forEach((p) => { p.hidden = p.getAttribute('data-sz-panel') !== key; });
      if (key === 'cond') sim(r);
      return;
    }
    const zone = e.target.closest('[data-sz-zone]');
    if (zone) { zone.classList.toggle('is-on'); sim(zone.closest('#pdp-sizing')); return; }
    const hit = e.target.closest('[data-sz-rect], [data-sz-row]');
    if (hit) activate(hit.closest('#pdp-sizing'), hit.getAttribute('data-sz-rect') || hit.getAttribute('data-sz-row'));
  });

  document.addEventListener('mouseover', (e) => {
    const el = e.target.closest && e.target.closest('[data-sz-rect], [data-sz-row]');
    if (el) activate(el.closest('#pdp-sizing'), el.getAttribute('data-sz-rect') || el.getAttribute('data-sz-row'));
  });

  const arm = () => {
    const r = document.getElementById('pdp-sizing');
    if (!r) return;
    const rows = r.querySelectorAll('[data-sz-row]');
    if (rows.length) activate(r, rows[rows.length - 1].getAttribute('data-sz-row'));
  };
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', arm); } else { arm(); }
})();

/* Drawer close X: try the theme's own toggle first (cart icon closes an
   open drawer), then hard-fallback to flipping aria-hidden. */
(() => {
  document.addEventListener('click', (event) => {
    const btn = event.target.closest && event.target.closest('[data-hod-drawer-close]');
    if (!btn) return;
    event.preventDefault();
    const toggle = document.querySelector('[data-action="toggle-mini-cart"], .header__cart-toggle, a[href="/cart"][data-drawer]');
    if (toggle) { toggle.click(); }
    setTimeout(() => {
      const mc = document.getElementById('mini-cart');
      if (mc && mc.getAttribute('aria-hidden') === 'false') {
        mc.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('cart-drawer-open');
        document.querySelectorAll('.drawer-overlay, [data-overlay]').forEach((o) => o.classList && o.classList.remove('is-visible'));
      }
    }, 150);
  });
})();

/* Nav hover intent (Stas, Aug 6): the theme opens menu dropdowns on raw
   mouseover, so grazing the nav while heading to content pops menus.
   Intercept the activating mouseover in the capture phase, wait 450ms,
   and only re-dispatch it if the cursor is still on the item. Keyboard
   (focusin) and touch paths are untouched. */
(() => {
  const DELAY = 300;
  const SEL = '[data-type="menuitem"][aria-haspopup]';
  let armed = null;
  let timer = null;
  if (!window.matchMedia('(hover: hover)').matches) return;
  const anyDropdownOpen = () => !!document.querySelector('.nav-bar ' + SEL + '[aria-expanded="true"]');
  document.addEventListener('mouseover', (e) => {
    if (!e.isTrusted) return; // let our synthetic re-dispatch through
    const item = e.target.closest ? e.target.closest(SEL) : null;
    if (!item || !item.closest('.nav-bar')) return;
    if (armed === item) return; // already open — keep theme behavior while inside
    if (anyDropdownOpen()) { armed = item; return; } // switching between menus: instant, keeps the overlay steady
    e.stopPropagation();
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (item.matches(':hover')) {
        armed = item;
        item.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
      }
    }, DELAY);
  }, true);
  document.addEventListener('mouseout', (e) => {
    const item = e.target.closest ? e.target.closest(SEL) : null;
    if (!item) return;
    if (!e.relatedTarget || !item.contains(e.relatedTarget)) {
      clearTimeout(timer);
      if (armed === item) armed = null;
    }
  }, true);
})();

/* cachebust 1786081581 */

/* Layout toggle sync (Stas, Aug 6): the theme's grid/list switch swaps
   card classes client-side but never syncs the container's
   product-list--list-view class (server-rendered from the cart
   attribute) — leaving grid stuck in the single-column list layout. */
(() => {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action="change-layout"]');
    if (!btn) return;
    const mode = btn.getAttribute('data-layout-mode');
    document.querySelectorAll('.product-list--collection').forEach((pl) => {
      pl.classList.toggle('product-list--list-view', mode === 'list');
    });
  });
})();

/* Filter UX (Stas, Aug 6): Show-more mode for long lists + mobile
   open-all. Re-applies after AJAX filter re-renders. */
(() => {
  const enhance = () => {
    ['desktop-filters-form', 'mobile-collection-filters-form'].forEach(enhanceForm);
  };
  const enhanceForm = (formId) => {
    const form = document.getElementById(formId);
    if (!form) return;
    /* open-all is an INITIAL state, applied once per rendered form — the
       MutationObserver re-runs enhance() on every DOM change, and without
       the marker it re-opened groups 150ms after the user collapsed them
       (filters looked un-collapsible on mobile). A fresh AJAX re-render
       replaces the form node, drops the marker, and gets opened again. */
    if (window.innerWidth < 1000 && form.dataset.mobileFilterMode === 'all_open' && !form.dataset.hodOpenedOnce) {
      form.dataset.hodOpenedOnce = '1';
      form.querySelectorAll('.collection__filter-group-name[aria-expanded="false"]').forEach((btn) => {
        const c = btn.nextElementSibling;
        if (!c || !c.classList.contains('collection__filter-collapsible')) return;
        /* Stas 8/24: mirror the desktop 8/13 rule — a list filter with a
           single unselected option stays collapsed (nothing to choose). */
        const boxes = c.querySelectorAll('input[type="checkbox"]');
        if (boxes.length === 1 && !boxes[0].checked) return;
        btn.setAttribute('aria-expanded', 'true');
        c.setAttribute('aria-hidden', 'false');
        c.style.height = 'auto';
      });
      /* Stas 8/24: nested spec sub-groups keep the server-rendered state
         (first group open, rest collapsed — same as desktop); the old
         force-open-all line is gone. */
    }
    if (form.dataset.filterOverflow === 'show_more') {
      form.querySelectorAll('.collection__filter-checkbox-list, .collection__filter-swatch-list').forEach((list) => {
        if (list.dataset.hodFmore) return;
        if (list.querySelectorAll(':scope > li').length <= 8) return;
        list.dataset.hodFmore = '1';
        list.classList.add('hod-fmore', 'hod-fmore--collapsed');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'hod-fmore-btn';
        btn.textContent = 'Show more';
        btn.addEventListener('click', () => {
          const collapsed = list.classList.toggle('hod-fmore--collapsed');
          btn.textContent = collapsed ? 'Show more' : 'Show less';
        });
        list.after(btn);
      });
    }
  };
  enhance();
  let t;
  new MutationObserver(() => { clearTimeout(t); t = setTimeout(enhance, 150); }).observe(document.body, { childList: true, subtree: true });
})();


/* Sort picker label sync (8/17, Stas bug report): theme.js reloads the
   product grid when a sort choice is picked but never re-renders this
   custom toolbar button, so it kept showing the previous choice. Update
   the label + selected state optimistically; the server renders the
   same wording on the next full load (see main-collection.liquid). */
(() => {
  document.addEventListener('click', (e) => {
    const choice = e.target.closest && e.target.closest('#sort-by-selector [data-action="select-value"]');
    if (!choice) return;
    const name = choice.textContent.replace(/\s+/g, ' ').trim();
    const btn = document.querySelector('.value-picker-button[aria-controls="sort-by-selector"]');
    if (btn) {
      btn.querySelectorAll('span').forEach((s) => {
        s.textContent = s.classList.contains('hidden-tablet-and-up') ? 'Sort: ' + name : name;
      });
    }
    const picker = choice.closest('.value-picker');
    if (picker) {
      picker.querySelectorAll('[data-action="select-value"]').forEach((b) => {
        b.classList.toggle('is-selected', b === choice);
      });
    }
  });
})();

/* Mobile sort select (8.7 drop): native control replaces the value
   picker on phones — OS sheet renders above Tidio by nature. */
(() => {
  document.addEventListener('change', (e) => {
    if (!e.target.matches || !e.target.matches('#hod-mobile-sort')) return;
    const u = new URL(location.href);
    u.searchParams.set('sort_by', e.target.value);
    u.searchParams.delete('page');
    location.href = u.href;
  });
})();

/* Filter sheet footer: "Show N results" with the live count. */
(() => {
  const update = () => {
    const btn = document.querySelector('#mobile-collection-filters .collection-drawer__footer button[data-action="close-drawer"]');
    if (!btn) return;
    const src = document.querySelector('.collection__showing-count, .collection__products-count-total');
    const m = src && src.textContent.match(/([\d,]+)\s*(products|results)?\s*$/i);
    const n = m ? m[1] : null;
    btn.textContent = n ? `Show ${n} results` : 'View results';
  };
  update();
  let t;
  new MutationObserver(() => { clearTimeout(t); t = setTimeout(update, 200); }).observe(document.body, { childList: true, subtree: true });
})();

/* Search UX (Stas, Aug 7): while the search popup is open, the first click
   outside it only CLOSES the search — it must not activate the link or
   button underneath. ESC closes it too. The header chrome is also dimmed +
   pointer-inert via CSS (hod-site.css) while open. */
(() => {
  const openSearch = () =>
    document.querySelector('.search-bar.is-expanded') ||
    document.querySelector('.header__search-bar-wrapper.is-fixed .search-bar');
  /* full close: is-expanded alone left .is-fixed behind, and the page fade
     (custom.css keys it to .is-fixed) survived until a second click */
  const closeSearch = (sb) => {
    const wrapper = sb.closest('.header__search-bar-wrapper');
    wrapper?.classList.remove('is-fixed');
    sb.classList.remove('is-expanded');
    sb.querySelector('.search-bar__results')?.setAttribute('aria-hidden', 'true');
    sb.querySelector('.search-bar__menu-wrapper')?.setAttribute('aria-hidden', 'true');
    sb.querySelector('.search-bar__input')?.blur();
    if (!document.querySelector('#mini-cart[aria-hidden="false"]')) {
      document.documentElement.classList.remove('is-locked');
    }
  };
  document.addEventListener('click', (e) => {
    const sb = openSearch();
    if (!sb || sb.contains(e.target)) return;
    e.preventDefault();
    e.stopPropagation();
    closeSearch(sb);
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const sb = openSearch();
    if (sb) closeSearch(sb);
  });
})();

/* cache-roll 2026-08-07T20:35Z */

/* Cart badge (Stas, Aug 7): hide the count bubble entirely at zero. */
(() => {
  let t;
  const update = () => {
    document.querySelectorAll('.header__cart-count').forEach((b) => {
      b.style.display = (parseInt(b.textContent, 10) || 0) === 0 ? 'none' : '';
    });
  };
  update();
  new MutationObserver(() => { clearTimeout(t); t = setTimeout(update, 150); })
    .observe(document.body, { childList: true, subtree: true, characterData: true });
})();

/* Mobile in-place search (Stas, Aug 7): the theme's is-fixed overlay is
   neutralized in CSS so the field never moves; this positions the
   suggestion panel under the field, scrolls to top first, and wires the
   Android back button to close instead of leaving the page. */
(() => {
  const mq = window.matchMedia('(max-width: 640px)');
  const wrapper = document.querySelector('.header__search-bar-wrapper');
  if (!wrapper) return;
  let pushed = false;
  const setPanelTop = () => {
    const sb = wrapper.querySelector('.search-bar');
    if (!sb) return;
    const bottom = Math.ceil(sb.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--hod-msearch-top', bottom + 'px');
  };
  const fullClose = () => {
    wrapper.classList.remove('is-fixed');
    wrapper.querySelector('.search-bar')?.classList.remove('is-expanded');
    wrapper.querySelector('.search-bar__results')?.setAttribute('aria-hidden', 'true');
    wrapper.querySelector('.search-bar__menu-wrapper')?.setAttribute('aria-hidden', 'true');
    wrapper.querySelector('.search-bar__input')?.blur();
    document.documentElement.classList.remove('is-locked');
  };
  new MutationObserver(() => {
    if (!mq.matches) return;
    if (wrapper.classList.contains('is-fixed')) {
      window.scrollTo(0, 0);
      setPanelTop();
      if (!pushed) { try { history.pushState({ hodSearch: 1 }, ''); pushed = true; } catch (e) {} }
    } else if (pushed) {
      pushed = false;
      if (history.state && history.state.hodSearch) history.back();
    }
  }).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('popstate', () => {
    if (pushed && mq.matches && wrapper.classList.contains('is-fixed')) {
      pushed = false;
      fullClose();
    }
  });
  /* the header search toggle doubles as the X while the panel is open */
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-action="toggle-search"]');
    if (!t || !mq.matches || !wrapper.classList.contains('is-fixed')) return;
    e.preventDefault();
    e.stopPropagation();
    fullClose();
  }, true);
})();

/* View-all pin (Stas, Aug 8): the theme renders the button INSIDE the
   scrolling results list, where position:sticky failed in the nested
   scroller — hoist it to the dropdown/panel root so it's always visible. */
(() => {
  let t;
  const hoist = () => {
    document.querySelectorAll('.search-bar__results .search-bar__view-all, .search-bar__results-inner .search-bar__view-all').forEach((btn) => {
      const inner = btn.closest('.search-bar__inner');
      if (!inner) return;
      /* the theme mints a NEW button on every keystroke re-render — drop
         any previously hoisted copy or they stack up (Stas bug report) */
      inner.querySelectorAll(':scope > .search-bar__view-all').forEach((old) => old.remove());
      inner.appendChild(btn);
    });
  };
  hoist();
  new MutationObserver(() => { clearTimeout(t); t = setTimeout(hoist, 80); })
    .observe(document.body, { childList: true, subtree: true });
})();

/* ============================================================
   8.10 Spotlight toolbar filter (Stas): dropdown in the left
   toolbar slot sets the filter.p.m.custom.spotlight param and
   reloads. Filtering takes effect once the Spotlight filter is
   enabled in Search & Discovery; label reflects the URL on load.
   ============================================================ */
(() => {
  const PARAM = 'filter.p.m.custom.spotlight';
  const root = document.querySelector('[data-hod-spotlight]');
  const pills = document.querySelector('[data-hod-spotlight-pills]');
  if (!root && !pills) return;
  const current = new URLSearchParams(location.search).get(PARAM) || '';
  document.querySelectorAll('[data-hod-spotlight-value]').forEach((btn) => {
    btn.classList.toggle('is-selected', btn.getAttribute('data-hod-spotlight-value') === current && current !== '');
  });
  if (root) {
    const toggle = root.querySelector('[data-hod-spotlight-toggle]');
    const menu = root.querySelector('[data-hod-spotlight-menu]');
    const label = root.querySelector('[data-hod-spotlight-label]');
    if (current && label) label.textContent = current;
    toggle.addEventListener('click', () => {
      const open = menu.hasAttribute('hidden');
      if (open) menu.removeAttribute('hidden'); else menu.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    document.addEventListener('click', (e) => {
      if (root.contains(e.target)) return;
      menu.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
    });
  }
  /* one handler for both the dropdown menu and the mobile pill row;
     tapping the already-active pill toggles the filter back off */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-hod-spotlight-value]');
    if (!btn) return;
    const url = new URL(location.href);
    let v = btn.getAttribute('data-hod-spotlight-value');
    if (v && v === current) v = '';
    if (v) url.searchParams.set(PARAM, v); else url.searchParams.delete(PARAM);
    url.searchParams.delete('page');
    location.assign(url.toString());
  });
})();

/* ============================================================
   HOD Quick View (design handoff 3a — Stas 8/19).
   Replaces the theme's quick view on COLLECTION CARDS only for
   now (the magnifier .product-item__qv-corner). Intercepts the
   click in the capture phase so the theme's modal never opens,
   fetches sections/hod-quick-view.liquid for the product via
   the Section Rendering API, and renders it in its own overlay.
   Any fetch failure (rate limit / challenge / offline) falls
   back to navigating to the PDP — never a broken modal.
   FBT rows, accessory lists, package cards and the Bundle
   Builder (3b) will be wired up in later passes.
   ============================================================ */
(() => {
  let overlay = null;
  let shell = null;
  let lastFocus = null;

  const ensureShell = () => {
    if (overlay) return;
    overlay = document.createElement('div');
    overlay.className = 'hqv-overlay';
    overlay.hidden = true;
    shell = document.createElement('div');
    shell.className = 'hqv-shell';
    shell.setAttribute('role', 'dialog');
    shell.setAttribute('aria-modal', 'true');
    shell.setAttribute('aria-label', 'Quick view');
    overlay.appendChild(shell);
    document.body.appendChild(overlay);

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target.closest('[data-hqv-close]')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !overlay.hidden) close();
    });
    shell.addEventListener('click', onShellClick);
  };

  const close = () => {
    overlay.hidden = true;
    shell.innerHTML = '';
    document.documentElement.classList.remove('hqv-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  };

  const setSlide = (root, idx) => {
    const slides = root.querySelectorAll('[data-hqv-slide]');
    const thumbs = root.querySelectorAll('[data-hqv-thumb]');
    if (!slides.length) return;
    const n = slides.length;
    const i = ((idx % n) + n) % n;
    slides.forEach((s) => s.classList.toggle('is-active', +s.getAttribute('data-hqv-slide') === i));
    thumbs.forEach((t) => t.classList.toggle('is-active', +t.getAttribute('data-hqv-thumb') === i));
    root.querySelectorAll('[data-hqv-goto]').forEach((p) => p.classList.toggle('is-active', +p.getAttribute('data-hqv-goto') === i));
    const activeThumb = root.querySelector('[data-hqv-thumb].is-active');
    if (activeThumb && activeThumb.scrollIntoView) activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const counter = root.querySelector('[data-hqv-counter]');
    if (counter) counter.textContent = (i + 1) + ' / ' + n;
    root.setAttribute('data-hqv-index', i);
  };

  const zoomSrc = (root) => {
    const active = root.querySelector('.hqv__slide.is-active');
    return active ? active.src.replace(/width=520/, 'width=1400') : null;
  };

  const closeZoom = () => {
    const z = shell.querySelector('.hqv__zoom');
    if (z) z.remove();
  };

  const openZoom = (root) => {
    closeZoom();
    const src = zoomSrc(root);
    if (!src) return;
    shell.scrollTop = 0;
    const z = document.createElement('div');
    z.className = 'hqv__zoom';
    z.style.height = shell.clientHeight + 'px';
    z.innerHTML =
      '<img src="' + src + '" alt="">' +
      '<button type="button" class="hqv__nav hqv__nav--prev" data-hqv-znav="prev" aria-label="Previous photo"><svg width="14" height="14" viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" fill="none" stroke="#104B6E" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<button type="button" class="hqv__nav hqv__nav--next" data-hqv-znav="next" aria-label="Next photo"><svg width="14" height="14" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" fill="none" stroke="#104B6E" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '<button type="button" class="hqv__zoom-close" data-hqv-zclose aria-label="Close enlarged image">✕</button>';
    shell.appendChild(z);
  };

  const onShellClick = (e) => {
    const root = shell.querySelector('[data-hqv]');
    if (!root) return;
    const cur = +(root.getAttribute('data-hqv-index') || 0);

    /* enlarged-image layer: nav flips the slide underneath and swaps the
       big image; any other click closes it */
    const zoomLayer = e.target.closest('.hqv__zoom');
    if (zoomLayer) {
      const znav = e.target.closest('[data-hqv-znav]');
      if (znav) {
        setSlide(root, znav.getAttribute('data-hqv-znav') === 'next' ? cur + 1 : cur - 1);
        const img = zoomLayer.querySelector('img');
        const src = zoomSrc(root);
        if (img && src) img.src = src;
      } else {
        closeZoom();
      }
      return;
    }
    if (e.target.closest('[data-hqv-zoombtn]') || e.target.closest('.hqv__slide.is-active')) {
      return openZoom(root);
    }

    const thumb = e.target.closest('[data-hqv-thumb]');
    if (thumb) return setSlide(root, +thumb.getAttribute('data-hqv-thumb'));
    const piece = e.target.closest('[data-hqv-goto]');
    if (piece) return setSlide(root, +piece.getAttribute('data-hqv-goto'));
    if (e.target.closest('[data-hqv-prev]')) return setSlide(root, cur - 1);
    if (e.target.closest('[data-hqv-next]')) return setSlide(root, cur + 1);

    const tab = e.target.closest('[data-hqv-tab]');
    if (tab) {
      const key = tab.getAttribute('data-hqv-tab');
      root.querySelectorAll('[data-hqv-tab]').forEach((t) => t.classList.toggle('is-active', t === tab));
      root.querySelectorAll('[data-hqv-pane]').forEach((p) => p.classList.toggle('is-active', p.getAttribute('data-hqv-pane') === key));
      return;
    }

    const more = e.target.closest('[data-hqv-more]');
    if (more) {
      const rest = root.querySelector('[data-hqv-rest]');
      if (rest) {
        const show = rest.hidden;
        rest.hidden = !show;
        more.textContent = show ? 'View less' : 'View more';
      }
      return;
    }

    const add = e.target.closest('[data-hqv-add]');
    if (add && !add.disabled && !add.hasAttribute('data-busy')) {
      add.setAttribute('data-busy', '1');
      fetch(window.routes && window.routes.cartAddUrl ? window.routes.cartAddUrl + '.js' : '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: +add.getAttribute('data-variant-id'), quantity: 1, properties: Object.assign(window.hodStockProps(add), { _added_via: 'Quick View' }) })
      }).then((r) => {
        if (!r.ok) throw new Error('add failed');
        const original = add.textContent;
        add.textContent = '✓ Added to Cart';
        /* same event + detail shape the theme's own add flow dispatches —
           refreshes the mini-cart and opens the drawer per theme setting */
        add.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { button: add, variant: null, quantity: 1, productAdded: true }
        }));
        setTimeout(() => {
          add.textContent = original;
          add.removeAttribute('data-busy');
        }, 1600);
      }).catch(() => {
        add.removeAttribute('data-busy');
        window.location.href = shell.querySelector('[data-hqv]').getAttribute('data-product-url');
      });
    }
  };

  const applyGiftCtx = (root) => {
    /* free-gift context (pdp-gift "View Details"): the gift is added
       automatically by the checked box in the parent's buy form — show
       FREE + struck-through real price, no Add to Cart. */
    const price = root.querySelector('.hqv__price');
    if (price) {
      const orig = price.textContent;
      price.textContent = 'FREE';
      price.classList.add('hqv__price--free');
      let cmp = root.querySelector('.hqv__compare');
      if (!cmp) {
        cmp = document.createElement('span');
        cmp.className = 'hqv__compare';
        price.after(cmp);
      }
      cmp.textContent = orig;
      const save = root.querySelector('.hqv__save');
      if (save) save.remove();
    }
    const add = root.querySelector('[data-hqv-add]');
    if (add) add.remove();
    const ctas = root.querySelector('.hqv__ctas');
    if (ctas) {
      const hint = document.createElement('div');
      hint.className = 'hqv__hint';
      hint.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style="flex:none"><rect x="3" y="9" width="18" height="12" rx="1.5" fill="none" stroke="#104B6E" stroke-width="1.8"/><path d="M3 13h18M12 9v12" stroke="#104B6E" stroke-width="1.8"/><path d="M12 9c-4.5 0-5.5-5-2.5-5S12 9 12 9zm0 0c4.5 0 5.5-5 2.5-5S12 9 12 9z" fill="none" stroke="#104B6E" stroke-width="1.6"/></svg><span>Included <b>FREE</b> with this purchase &mdash; added automatically at checkout.</span>';
      ctas.before(hint);
    }
  };

  const applyBundleCtx = (root, card) => {
    /* Bundle Builder context (design 3b): CTA zone becomes a slot hint +
       a full-width Add to Bundle toggle. Add = programmatic click on the
       card's own "+ Add to Bundle" button so ALL builder logic runs
       untouched; remove = click on the slot's own ✕ then close the modal
       (the builder collapses the chooser after an add, so a re-add must
       happen from the builder itself). */
    const slotApp = (document.querySelector('[data-app].is-active') || {getAttribute: () => ''}).getAttribute('data-app') || '';
    const bbSlot = card.getAttribute ? card.getAttribute('data-bb-slotlabel') : '';
    const slotLabel = bbSlot || slotApp.split('-')[0] || 'this';
    const bbBrandEl = document.querySelector('[data-bb-brandname]');
    const brand = (bbBrandEl && bbBrandEl.textContent.trim()) || (document.querySelector('[data-appliance-options]') || {getAttribute: () => ''}).getAttribute('data-appliance-brand') || '';
    const ctas = root.querySelector('.hqv__ctas');
    if (!ctas) return;

    /* G.2 (8/21): the new hod-bundle-builder marks its cards data-bb-card
       and keeps an is-inbundle class in sync — no legacy slot DOM needed */
    const managed = card.hasAttribute('data-bb-card');
    const slotEl = () => document.querySelector('.bundle-builder--app-item[data-app="' + slotApp + '"]');
    const inBundle = () => {
      if (managed) return card.classList.contains('is-inbundle');
      const el = slotEl();
      if (!el || !el.classList.contains('bundle-builder--app-item--selected')) return false;
      const t = (el.querySelector('.bundle-builder--app-item--title') || {textContent: ''}).textContent.trim();
      const v = (el.querySelector('.bundle-builder--app-item--variant') || {textContent: ''}).textContent.trim();
      return t === (card.getAttribute('data-title') || '').trim() && v === (card.getAttribute('data-variant') || '').trim();
    };

    const hint = document.createElement('div');
    hint.className = 'hqv__hint';
    hint.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" style="flex:none"><rect x="3" y="8" width="18" height="12" rx="2" fill="none" stroke="#104B6E" stroke-width="1.8"/><path d="M8 8V6a4 4 0 018 0v2" fill="none" stroke="#104B6E" stroke-width="1.8"/></svg><span>Fills the <b>' + slotLabel + '</b> slot in your ' + (brand ? brand + ' ' : '') + 'bundle &mdash; one per bundle.</span>';

    const cardAddBtn = card.querySelector('.bundle-builder--choose-add');
    const soldOut = !cardAddBtn || cardAddBtn.classList.contains('bundle-builder--choose-add--out');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hqv__btn hqv__btn--bundle';
    const paint = () => {
      if (soldOut) { btn.textContent = 'Out of Stock'; btn.disabled = true; return; }
      const on = inBundle();
      btn.classList.toggle('is-in', on);
      btn.textContent = on ? '✓ In Bundle — tap to remove' : '+ Add to Bundle';
    };
    btn.addEventListener('click', () => {
      if (soldOut) return;
      if (managed) {
        /* the new builder's add button toggles add/remove itself */
        if (cardAddBtn) cardAddBtn.click();
        close();
        return;
      }
      if (inBundle()) {
        const rm = slotEl() && slotEl().querySelector('.bundle-builder--app-item--remove');
        if (rm) rm.click();
        close();
      } else if (card.isConnected && cardAddBtn) {
        cardAddBtn.click();
        setTimeout(paint, 60);
      }
    });
    paint();

    /* Stas 8/20: secondary non-CTA returns to the chooser list */
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'hqv__btn hqv__btn--view';
    back.textContent = 'Back to Options';
    back.addEventListener('click', close);

    ctas.replaceChildren(back, btn);
    ctas.before(hint);
  };

  const open = (productUrl, trigger, ctx, card) => {
    ensureShell();
    lastFocus = trigger || document.activeElement;
    shell.innerHTML = '';
    shell.classList.add('is-loading');
    overlay.hidden = false;
    document.documentElement.classList.add('hqv-lock');
    const base = productUrl.split('?')[0];
    let qs = '?section_id=hod-quick-view';
    if (ctx === 'bundle' && card) {
      const vid = (card.getAttribute('data-id') || '').replace(/\D/g, '');
      if (vid) qs += '&variant=' + vid;
    }
    fetch(base + qs, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('bad status'))))
      .then((html) => {
        const frag = document.createRange().createContextualFragment(html);
        const content = frag.querySelector('[data-hqv]');
        if (!content) throw new Error('no fragment');
        shell.classList.remove('is-loading');
        shell.replaceChildren(content);
        if (ctx === 'gift') applyGiftCtx(content);
        if (ctx === 'bundle' && card) applyBundleCtx(content, card);
        const closeBtn = shell.querySelector('[data-hqv-close]');
        if (closeBtn) closeBtn.focus();
      })
      .catch(() => {
        /* rate-limited / challenged / offline: go to the PDP instead */
        close();
        window.location.href = productUrl;
      });
  };

  /* triggers: collection-card magnifier + PDP FBT rows and accessory
     lists (both share .fbt__qv). Bundle Builder (3b) and gift QV still
     use the theme quick view until their passes. */
  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('.product-item__qv-corner, .fbt__qv, .pdp-gift__view, .hod-gift__view, .bundle-builder--choose-view, .bundle-builder--choose-item__title[data-secondary-action]');
    if (!t) return;
    const url = t.getAttribute('data-product-url');
    if (!url) return;
    e.preventDefault();
    e.stopPropagation();
    const bundleCard = t.closest('.bundle-builder--choose-item');
    const ctx = bundleCard ? 'bundle' : (t.classList.contains('pdp-gift__view') || t.classList.contains('hod-gift__view') ? 'gift' : null);
    open(url, t, ctx, bundleCard);
  }, true);
})();


/* ------------------------------------------------------------------
   PDP carousels (You may also like / Recently viewed) load their
   cards AFTER page load (recommendations fetch + recently-viewed JS),
   so the initial fractional-star pass misses them — no 4.86 number.
   Watch those sections and re-run when cards arrive (8/20). The
   star rewriter tags processed nodes, so re-runs are no-ops.
   ------------------------------------------------------------------ */
(() => {
  const arm = () => {
    document.querySelectorAll('[id*="product-recommendations"], [id*="recently-viewed"]').forEach((sec) => {
      if (sec.hasAttribute('data-hod-stars-watch')) return;
      sec.setAttribute('data-hod-stars-watch', '1');
      new MutationObserver(() => {
        if (typeof window.hodFractionalStars === 'function') window.hodFractionalStars();
      }).observe(sec, { childList: true, subtree: true });
    });
    if (typeof window.hodFractionalStars === 'function') window.hodFractionalStars();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm);
  else arm();
})();

/* E.1 r2 (Stas 8/20): hide the Tidio chat bubble while a Judge.me photo
   popup (jm-mfp Magnific fork) is open — it overlapped the popup on
   mobile. The wrap is appended to/removed from <body> on open/close. */
(() => {
  const sync = () => document.documentElement.classList.toggle('hod-jdgm-open', !!document.querySelector('.jm-mfp-wrap'));
  if (document.body) {
    new MutationObserver(sync).observe(document.body, { childList: true });
  }
})();

/* ------------------------------------------------------------------
   F.1 (8/21): free gift attach + lifecycle.
   pdp-gift no longer posts legacy items[N] inputs (Form.serialize
   mangled them and the gift never reached the cart on 3.9). Instead:
   after any successful parent add (theme buy-button, bundle
   interceptor, HOD quick view via data-gift-variant on its button),
   the gift is added here with _freegift/_freegiftparent properties —
   Vitali's "Free Gift" app discount function zeroes the line.
   Lifecycle (Stas 8/20): the sweeper removes gift lines whose parent
   left the cart, which also kills gift-only carts.
   ------------------------------------------------------------------ */
(() => {
  window.hodAttachGift = async (giftVariant, parentVariant) => {
    const gid = parseInt(giftVariant, 10);
    if (!gid) return;
    try {
      /* skip when this exact gift-for-parent line is already in the cart
         (re-adding would bump its quantity) */
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      const dup = cart.items.some((i) => i.variant_id === gid && i.properties && i.properties._freegift);
      if (dup) return;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: gid, quantity: 1, properties: { '_freegift': 'true', '_freegiftparent': String(parentVariant || '') } }] })
      });
      if (res.ok) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) { /* gift add is best-effort; the parent itself was added */ }
  };

  document.documentElement.addEventListener('product:added', (e) => {
    const d = e.detail || {};
    if (!d.button || !d.button.closest) return; /* FBT/accessory dispatches carry no button */
    /* HOD quick view: the add button carries the gift directly */
    const btnGift = d.button.getAttribute && d.button.getAttribute('data-gift-variant');
    if (btnGift) {
      window.hodAttachGift(btnGift, d.button.getAttribute('data-variant-id') || '');
      return;
    }
    /* main PDP / featured product / bundle interceptor: find the gift
       block in the same section, honor the opt-out checkbox */
    const scope = d.button.closest('[data-section-type]') || d.button.closest('form') || document;
    const gift = scope.querySelector('[data-hod-gift]');
    if (!gift) return;
    const toggle = gift.querySelector('[data-hod-gift-toggle]');
    if (toggle && !toggle.checked) return;
    const form = d.button.closest('form');
    const idInput = form && (form.querySelector('[name="id"]') || form.querySelector('[name="id[]"]') || form.querySelector('.main_product_sku'));
    window.hodAttachGift(gift.getAttribute('data-gift-variant'), idInput ? idInput.value : '');
  });

  /* orphan sweeper: a gift's parent counts as present when any non-gift
     line matches its variant id, or carries _bundle_id equal to it
     (cart-transform packages keep the parent variant id on each piece). */
  let sweeping = false;
  const sweep = async () => {
    if (sweeping) return;
    sweeping = true;
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      const others = cart.items.filter((i) => !(i.properties && i.properties._freegift));
      const present = (pid) => pid && others.some((i) => String(i.variant_id) === pid || (i.properties && String(i.properties._bundle_id || '') === pid));
      const orphans = cart.items.filter((i) => i.properties && i.properties._freegift && !present(String(i.properties._freegiftparent || '')));
      let removed = false;
      for (const o of orphans) {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ id: o.key, quantity: 0 })
        });
        if (res.ok) removed = true;
      }
      sweeping = false;
      if (removed) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) { sweeping = false; }
  };
  document.documentElement.addEventListener('cart:refresh', () => setTimeout(sweep, 400));
  if (document.body && document.body.classList.contains('template-cart')) setTimeout(sweep, 800);
})();


/* K.3.3 (8/24): Escape closes the new builder overlays (kitchen + mini split)
   and announces nothing else — theme modals already handle their own Escape. */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const ov = document.querySelector('.hodbb__overlay:not([hidden]), .hodms__overlay:not([hidden])');
  if (!ov) return;
  const x = ov.querySelector('[aria-label="Close"], .hodbb__modal-x, .hodms__modal-x');
  if (x) x.click();
});


/* K.3.3 (8/24): the Bread pre-qual iframe (#PBarNextFrame) ships without a
   title — name it when it appears so every page passes frame-title. */
(() => {
  const nameFrames = () => document.querySelectorAll('iframe#PBarNextFrame:not([title])')
    .forEach((f) => f.setAttribute('title', 'Bread payment options'));
  nameFrames();
  let tries = 0;
  const iv = setInterval(() => { nameFrames(); if (++tries > 20) clearInterval(iv); }, 1500);
})();


/* ================================================================
   K r2 (8/24): announcements, focus trap, slideshow slide focus.
   ================================================================ */
(() => {
  const announce = (msg) => {
    const r = document.getElementById('a11y-status');
    if (!r) return;
    r.textContent = '';
    setTimeout(() => { r.textContent = msg; }, 60);
  };
  window.hodAnnounce = announce;

  /* cart events (theme.js buy buttons + our FBT/undo dispatchers) */
  document.addEventListener('product:added', (e) => {
    const q = e.detail && e.detail.quantity ? e.detail.quantity : 1;
    announce(q > 1 ? q + ' items added to cart' : 'Added to cart');
  }, true);
  document.addEventListener('cart:rerendered', () => announce('Cart updated'), true);

  /* filter results: announce the new product count after an apply */
  let lastCount = null;
  const watchCount = () => {
    const src = document.querySelector('.collection__showing-count, .collection__products-count-total');
    if (!src) return;
    const m = src.textContent.match(/([\d,]+)\s*(products|results)?\s*$/i);
    if (m) {
      if (lastCount !== null && m[1] !== lastCount) announce(m[1] + ' products');
      lastCount = m[1];
    }
  };
  let wc;
  new MutationObserver(() => { clearTimeout(wc); wc = setTimeout(watchCount, 250); }).observe(document.body, { childList: true, subtree: true });
  watchCount();

  /* keep Tab inside open builder overlays + the lightbox */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const dlg = document.querySelector('.hodbb__overlay:not([hidden]) [role="dialog"], .hodms__overlay:not([hidden]) [role="dialog"], .hod-lightbox');
    if (!dlg) return;
    const f = [...dlg.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    else if (!dlg.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
  });

  /* hidden carousel slides leave the tab order (Flickity marks them aria-hidden) */
  const syncSlides = () => document.querySelectorAll('.slideshow__slide').forEach((s) => {
    if (s.getAttribute('aria-hidden') === 'true') s.setAttribute('tabindex', '-1');
    else s.removeAttribute('tabindex');
  });
  document.querySelectorAll('.slideshow').forEach((el) => {
    new MutationObserver(syncSlides).observe(el, { attributes: true, subtree: true, attributeFilter: ['aria-hidden'] });
  });
  syncSlides();
})();

/* CPS cart offer collapse (Stas 8/27): "No plan" is no longer pre-selected;
   clicking it folds the offer into a slim "Protection plan: none" row,
   remembered per covered variant for the session (cart re-renders keep it
   folded). "Add coverage" reopens. */
(() => {
  const KEY = 'hodPPCollapsed';
  const load = () => { try { return new Set(JSON.parse(sessionStorage.getItem(KEY) || '[]')); } catch (e) { return new Set(); } };
  const save = (set) => { try { sessionStorage.setItem(KEY, JSON.stringify([...set])); } catch (e) {} };
  const apply = () => {
    const set = load();
    document.querySelectorAll('[data-pp-cart-offer]').forEach((box) => {
      box.classList.toggle('pp-cart--collapsed', set.has(box.getAttribute('data-covers-variant')));
    });
  };
  document.addEventListener('click', (event) => {
    const none = event.target.closest && event.target.closest('[data-pp-cart-none]');
    if (none) {
      const box = none.closest('[data-pp-cart-offer]');
      if (!box) return;
      const set = load(); set.add(box.getAttribute('data-covers-variant')); save(set);
      box.classList.add('pp-cart--collapsed');
      return;
    }
    const reopen = event.target.closest && event.target.closest('[data-pp-cart-reopen]');
    if (reopen) {
      const box = reopen.closest('[data-pp-cart-offer]');
      if (!box) return;
      const set = load(); set.delete(box.getAttribute('data-covers-variant')); save(set);
      box.classList.remove('pp-cart--collapsed');
    }
  });
  document.addEventListener('cart:refresh', () => { setTimeout(apply, 400); setTimeout(apply, 1200); }, true);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', apply);
  else apply();
})();

/* PDP gallery thumbnail guard (Stas 8/28): thumbnails are raw <a href="...jpg">
   links and rely on the theme's delegated click handler to preventDefault. When
   that handler isn't alive at click time (early click before init, re-rendered
   gallery), the browser navigates to the bare image file. Capture-phase guard:
   always block the raw link; if the theme handler is dead, jump the carousel
   ourselves via the Flickity instance. */
document.addEventListener('click', (event) => {
  const thumb = event.target.closest && event.target.closest('.product-gallery__thumbnail');
  if (!thumb) return;
  event.preventDefault();
  /* race fallback: if the theme's delegated handler wasn't bound yet, the
     selection won't move — re-dispatch one synthetic click shortly after
     (marked to avoid loops), by which time init has usually completed. */
  if (event.isTrusted && !thumb.dataset.hodRetried && !thumb.classList.contains('is-nav-selected')) {
    setTimeout(() => {
      if (thumb.classList.contains('is-nav-selected')) return;
      thumb.dataset.hodRetried = '1';
      thumb.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      setTimeout(() => { delete thumb.dataset.hodRetried; }, 1500);
    }, 450);
  }
}, true);
